// ============================================================================
// chatView.js — a csevegő képernyő megjelenítése.
//
// A megjelenítendő szöveg kiválasztása a SPECIFICATION.md 10. szakaszának
// sorrendjét követi. Minden szöveg textContent-tel kerül a DOM-ba (soha
// nem innerHTML), így a felhasználói tartalom nem értelmeződik HTML-ként.
// ============================================================================

import { t, localeFor, CHAT_LANGUAGES } from "./i18n.js";
import { renderAtiratSor, renderAtirat } from "./atiratView.js";
import { renderSzavazo } from "./pollView.js";
import { el } from "./dom.js";
import { renderListaJelzes } from "./listaJelzes.js";
import { vagolapraMasol } from "./vagolap.js";
import { ACCEPTED_TYPES, MAX_KEPEK } from "./imageUpload.js";
import { pickSenderName } from "./nameVariants.js";
import { darabolasiKorlat, splitMessage } from "./messageSplit.js";
import { kepCim } from "./kepBetolto.js";
import { nyissMegKepet } from "./kepNezo.js";
import { szobaFaSorrend, szuloUtvonal, szuloSzobaIdk } from "./roomTree.js";
import { log } from "./log.js";
import { markdownCsomopontok } from "./markdown.js";
import { szerkesztesiIdonBelul, torlesiIdonBelul } from "./messageWindows.js";
import { kapcsolatKulcs, temaCim } from "./forumData.js";
import { renderFordJavaslat } from "./fordJavaslatView.js";
import { renderBejaratFej } from "./bejaratFej.js";
import { renderTopicList, renderTopicBar, renderTitlesEditor, renderTagsEditor, renderCatalogEditor, renderPostTags, renderPostFilter, renderSubRooms, renderSzobaUtvonal, renderTemaUtvonal, renderPostLinks, renderSourceCard, renderSourceEditor, renderWorkflowNote, renderWorkflowPanel, renderPecsetek, renderAtnevezes } from "./forumView.js";
import { uzenetSzures, hasznaltCimkek } from "./forumData.js";
import { fajlMeret, fajlHivatkozas } from "./pdfCsatolas.js";
import { getRoomDisplayName } from "./roomName.js";

// A szoba megjelenített neve: roomName.js (az admin felület is ezt használja). Az export itt marad (régi hívók).
export { getRoomDisplayName };

/**
 * Egy csatolt kép megjelenítése (koppintásra nyíló gomb a képpel), vagy null, ha az adatból nem jeleníthető meg.
 *
 * A képet a szöveg ELÉ tesszük: a csevegésben a kép a fő tartalom, a szöveg a hozzá tartozó megjegyzés.
 * A width/height attribútumot azért adjuk meg, hogy a böngésző még a kép letöltése ELŐTT tudja, mekkora helyet hagyjon -
 * enélkül a lista megugrana minden egyes kép betöltődésekor.
 *
 * Csak https:// cím jelenhet meg. A szerver (sendMessage validateImage) ennél jóval szigorúbb, de a cím egy gombon át
 * nyílik meg: egy régebbi vagy kézzel írt adatból érkező "javascript:" cím kattintásra kódot futtatna. Ez a sor az
 * utolsó védvonal (AUDIT, 2026-09-26).
 */
function kepKapcsolo(adat, uiLang) {
  const kepUrl = adat && typeof adat.url === "string" && /^https:\/\//i.test(adat.url) ? adat.url : null;
  if (!adat || !((typeof adat.path === "string" && adat.path !== "") || kepUrl)) return null;
  // A képet HITELESÍTETTEN töltjük le (lásd kepBetolto.js): a nyilvános letöltési URL egy hordozható kulcs lenne, amivel
  // bejelentkezés nélkül is megnyitható a kép. A blob-cím csak ezen a lapon él. Koppintásra a saját nézegető nyílik meg
  // (nagy kép + letöltés + megosztás). MIÉRT nem sima hivatkozás új lapra: a kép `blob:` címen érkezik, ami CSAK ezen a
  // lapon érvényes - új lapon semmit nem jelentene.
  const link = el("button", {
    type: "button",
    class: "msg-image-link",
    title: t(uiLang, "image.openFull"),
  });
  const kep = el("img", {
    class: "msg-image",
    alt: t(uiLang, "image.alt"),
    decoding: "async",
  });
  // A cím aszinkron érkezik. Amíg nincs meg, a helyfoglalás a méretarányból már látszik, tehát a lista nem ugrik meg.
  // v222 (#67): ha van bélyegkép (thumbPath), a LISTÁBAN az jelenik meg (~14x kisebb forgalom), a teljes kép koppintásra töltődik be.
  // A bélyeg bármilyen hibája (áthelyezett téma után más szoba tárolójában, törölt fájl) esetén a teljes kép a tartalék.
  const teljesBetoltese = () => kepCim(adat.path, kepUrl).then((cim) => {
    if (!cim) {
      // Nincs jogosultság vagy hálózati hiba: a kép helyén jelzés marad.
      kep.alt = t(uiLang, "image.unavailable");
      kep.classList.add("msg-image-missing");
      return null;
    }
    return cim;
  });
  const thumbUrl = typeof adat.thumbUrl === "string" && /^https:\/\//i.test(adat.thumbUrl) ? adat.thumbUrl : null;
  if (typeof adat.thumbPath === "string" && adat.thumbPath !== "") {
    kep.addEventListener("error", () => {
      if (kep.dataset.bely === "fallback") return;
      kep.dataset.bely = "fallback";
      teljesBetoltese().then((cim) => { if (cim) kep.src = cim; });
    });
    kepCim(adat.thumbPath, thumbUrl).then((cim) => {
      if (cim) kep.src = cim;
      else teljesBetoltese().then((teljes) => { if (teljes) kep.src = teljes; });
    });
    link.onclick = () => { teljesBetoltese().then((teljes) => { if (teljes) nyissMegKepet(teljes, uiLang); }); };
  } else {
    teljesBetoltese().then((cim) => {
      if (!cim) return;
      kep.src = cim;
      link.onclick = () => nyissMegKepet(cim, uiLang);
    });
  }
  if (adat.width && adat.height) {
    kep.setAttribute("width", adat.width);
    kep.setAttribute("height", adat.height);
    // Az arány megőrzése a CSS-ben: így a helyfoglalás pontos.
    kep.style.aspectRatio = `${adat.width} / ${adat.height}`;
  }
  link.appendChild(kep);
  return link;
}

/**
 * Egy üzenethez kiválasztja a megjelenítendő szöveget és az állapotjelzést.
 *
 * A `lang` a megjelenített szöveg nyelve (fordításnál a néző nyelve, egyébként
 * a forrásnyelv). A nézet ezt a szöveg `lang` attribútumába írja: a böngésző
 * ebből választ betűformát (ugyanaz az írásjel japánul, egyszerűsített és
 * hagyományos kínaiul eltérően néz ki), és a felolvasó is ebből tudja a nyelvet.
 */
export function chooseDisplayText(msg, viewerLang, uiLang) {
  const eredmeny = valasszSzoveget(msg, viewerLang, uiLang);
  const forras = typeof msg.sourceLang === "string" && msg.sourceLang ? msg.sourceLang : null;
  eredmeny.lang = eredmeny.isTranslated ? viewerLang : forras;
  return eredmeny;
}

// A napi korlát miatt lezárt fordítás (worker.js: COST_LIMIT = a napi
// költségplafon, QUOTA_EXCEEDED = a napi fordítási darabszám) nem "hiba":
// a tag lássa, hogy a fordítás aznapra szünetel.
const NAPI_KORLAT_KODOK = ["COST_LIMIT", "QUOTA_EXCEEDED"];

function valasszSzoveget(msg, viewerLang, uiLang) {
  const original = typeof msg.text === "string" ? msg.text : "";
  const translations = msg.translations || {};
  const targetLangs = Array.isArray(msg.targetLangs) ? msg.targetLangs : [];

  // 1. Szándékosan nem fordított üzenet.
  //    Lakat ikont NEM használunk: ez nem titkosítás (SPECIFICATION.md 10.).
  //    Saját felirat (2026-10-08, tagi kérésre): "a küldő kérésére nem fordított" - így nem keverhető
  //    össze a többi kihagyással (pl. küldéskor még nem volt más nyelvű tag: "nem fordított").
  if (msg.noTranslate === true || msg.skipReason === "NO_TRANSLATE") {
    return {
      text: original,
      note: msg.sourceLang === viewerLang ? null : t(uiLang, "chat.statusNoTranslateBySender"),
      isTranslated: false,
    };
  }

  // 1b. Betű nélküli üzenet (csak emoji / szám / link; 2026-10-08, költség): a szerver szándékosan
  //     nem fordítja, és nincs mit fordítani - jelzés nélkül az eredeti a helyes minden nyelven.
  if (msg.skipReason === "NO_LETTERS") {
    return { text: original, note: null, isTranslated: false };
  }

  // 2. A néző nyelve = a forrásnyelv: az eredeti szöveg a helyes.
  if (msg.sourceLang === viewerLang) {
    return { text: original, note: null, isTranslated: false };
  }

  // 3. Van validált fordítás a néző nyelvére.
  const translated = translations[viewerLang];
  if (typeof translated === "string" && translated.length > 0) {
    return { text: translated, note: null, isTranslated: true, original };
  }

  // 4. A néző nyelve nincs az üzenet célnyelvei között.
  if (targetLangs.length > 0 && !targetLangs.includes(viewerLang)) {
    return { text: original, note: t(uiLang, "chat.statusNoTargetLang"), isTranslated: false };
  }

  // 5. Végleges hiba, vagy 'done' hiányzó fordítással.
  // A fordítás hibája "újrafordítható" (2026-10-07, tagi kérésre): a tag kényszerített
  // újrafordítást kérhet. Kivétel, ha az üzemeltető szándékosan leállította a fordítást
  // (SERVICE_PAUSED) - ilyenkor a szerver úgyis elutasítaná.
  const ujrafordithato = msg.errorCode !== "SERVICE_PAUSED";
  if (msg.translationStatus === "error" && NAPI_KORLAT_KODOK.includes(msg.errorCode)) {
    return { text: original, note: t(uiLang, "chat.statusPaused"), isTranslated: false, ujrafordithato };
  }
  if (msg.translationStatus === "error" || msg.translationStatus === "done") {
    return { text: original, note: t(uiLang, "chat.statusError"), isTranslated: false, ujrafordithato };
  }

  // 7. Egyéb kihagyás (pl. nincs célnyelv).
  if (msg.translationStatus === "skipped") {
    return { text: original, note: t(uiLang, "chat.statusSkipped"), isTranslated: false };
  }

  // 6. Folyamatban (pending / processing / retry_wait).
  return { text: original, note: t(uiLang, "chat.statusPending"), isTranslated: false };
}

/** Gomb tartalma: ikon + alatta kis felirat. Csak változáskor épül újra (a fókusz és a kattintás nem szakad meg). */
function gombTartalom(gomb, ikon, felirat) {
  if (gomb.dataset.ikon === ikon && gomb.dataset.felirat === felirat) return;
  gomb.dataset.ikon = ikon;
  gomb.dataset.felirat = felirat;
  gomb.textContent = "";
  gomb.appendChild(el("span", { class: "btn-ico", text: ikon }));
  gomb.appendChild(el("span", { class: "btn-label", text: felirat }));
}

/** Melyik csatolási módot jelzi a mező tartalma? A parancs csak akkor él, ha a szöveg PONTOSAN "@1" / "@2" (szerver: aiParancs). */
export function kepModBolSzoveg(draft) {
  const s = typeof draft === "string" ? draft.trim() : "";
  return s === "@2" ? "ocr" : s === "@1" ? "enhance" : "normal";
}

function kepMod(draft, uiLang, sending, handlers) {
  const aktualis = kepModBolSzoveg(draft);
  const doboz = el("div", { class: "image-mode", role: "group", "aria-label": t(uiLang, "attach.imageOrPdf") });
  const sor = el("div", { class: "image-mode-row" });
  for (const [mod, kulcs] of [["normal", "image.modeNormal"], ["ocr", "image.modeOcr"], ["enhance", "image.modeEnhance"]]) {
    sor.appendChild(el("button", {
      type: "button", class: "image-mode-btn" + (aktualis === mod ? " active" : ""), text: t(uiLang, kulcs),
      "aria-pressed": aktualis === mod ? "true" : "false", disabled: sending, onclick: () => handlers.onPickImageMode(mod),
    }));
  }
  doboz.appendChild(sor);
  if (aktualis !== "normal") doboz.appendChild(el("div", { class: "image-mode-hint", text: t(uiLang, aktualis === "ocr" ? "image.modeHintOcr" : "image.modeHintEnhance") }));
  return doboz;
}


/**
 * Időbélyeg formázása a felhasználó locale-ja szerint.
 * Mai üzenetnél csak az idő, tegnaptól "Tegnap HH:MM", régebbitől dátum + idő.
 */
function formatTime(ts, uiLang) {
  if (!ts) return "";
  // A Firestore Timestamp objektum .toDate()-del Date-té alakítható;
  // ha már Date, vagy ms-os szám, azt is kezeljük.
  let date;
  if (ts && typeof ts.toDate === "function") {
    date = ts.toDate();
  } else if (ts && ts.seconds) {
    date = new Date(ts.seconds * 1000);
  } else {
    date = new Date(ts);
  }
  if (isNaN(date.getTime())) return "";

  const locale = localeFor(uiLang);
  const now = new Date();
  const timeStr = date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });

  if (date.toDateString() === now.toDateString()) {
    return timeStr;
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return t(uiLang, "msg.yesterday") + " " + timeStr;
  }
  return date.toLocaleDateString(locale, { month: "short", day: "numeric" }) + " " + timeStr;
}

/**
 * Egy üzenetsor ALÁÍRÁSA: minden, amitől a sor kinézete függ. Ha két
 * render között nem változik, a sor DOM-eleme újrahasználható.
 *
 * Benne van: maga az üzenet (szöveg, fordítások, állapot, kép, idézet),
 * a néző nyelvei és azonosítója, a mai nap (a "Tegnap"/időpont felirat
 * éjfélkor változik), és a megválaszolt üzenet AKTUÁLIS megjelenített
 * szövege - az idézet az eredeti üzenet fordításával frissül.
 *
 * null = nem képezhető (pl. váratlan adat) - ilyenkor mindig újraépül.
 */
function sorAlairas(msg, viewerLang, uiLang, myUid, osszesUzenet, ma, sajatSzerkesztesAllapot, megjelenitesiAllapot) {
  try {
    let idezett = null;
    if (msg.replyTo && msg.replyTo.messageId && Array.isArray(osszesUzenet)) {
      const eredeti = osszesUzenet.find((x) => x.messageId === msg.replyTo.messageId);
      if (eredeti) {
        idezett = [chooseDisplayText(eredeti, viewerLang, uiLang).text, Boolean(eredeti.image && (eredeti.image.path || eredeti.image.url))];
      }
    }
    // A szerkesztés állapota (2026-10-02) NEM az üzenet dokumentum része,
    // ezért a `msg` önmagában nem jelezné, hogy EZT az üzenetet épp
    // szerkeszti a felhasználó - enélkül a szerkesztő mező megnyitása/
    // bezárása nem váltana ki újrarajzolást.
    // A megjelenítési állapot (2026-10-07): az eredeti/fordítás átváltása, a folyamatban lévő
    // újrafordítás és az, hogy a szerkesztés/törlés gomb még kínálható-e (az idő múlásától függ).
    // Ezek sem az üzenet dokumentumának részei - enélkül a változásuk nem rajzolna újra.
    return JSON.stringify([viewerLang, uiLang, myUid, ma, msg, idezett, sajatSzerkesztesAllapot, megjelenitesiAllapot]);
  } catch (e) {
    return null;
  }
}

/**
 * A feladó megjelenített neve.
 *
 * A NÉV ÍRÁSMÓDJA a TARTALOM nyelvét követi (`viewerLang` - az a nyelv,
 * amelyikre az üzenet le van, vagy lenne fordítva), NEM a felület nyelvét
 * (2026-09-30, tagi kérésre: "ha a tag írását az adott nyelvre lefordítják,
 * a neve is az adott nyelvhez tartozó beállítással jelenjen meg"). A
 * rendszerfeliratok ("Törölt tag", "Ismeretlen") viszont a felület
 * nyelvén jelennek meg (`uiLang`) - azok nem a küldő neve, hanem a
 * program szövege.
 *
 * Ha az üzenet feladóját törölték (senderDeleted, lásd
 * functions/src/lib/userPurge.js), a név már nincs meg.
 */
/**
 * Írhat-e a néző ebben a szobában? (2026-10-08) A csak olvasható (readOnly) szobában csak admin.
 * A döntést a szerver kényszeríti ki (sendMessage / editMessage / storage.rules); ez csak a felület.
 */
export function szobaIrhato(room, isAdmin) {
  return !(room && room.readOnly === true) || isAdmin === true;
}

/**
 * Milyen fordítás-gomb tartozik az üzenet alá a néző számára? (2026-10-08, tagi kérésre)
 *   null      - nincs gomb (törölt, szöveg / betű nélküli, folyamatban lévő, a néző nyelvén írt üzenet,
 *               vagy szünetel a fordítás);
 *   "force"   - kényszerített FORDÍTÁS: a "#" jellel szándékosan nem fordított, vagy a néző nyelvére
 *               nem fordított (célnyelv nélkül kihagyott) üzenet;
 *   "retry"   - ÚJRAFORDÍTÁS: kész (akár hibátlan) vagy hibás fordítás.
 * A szerver (messageActions.js retranslationEligibility) ugyanezt engedi; a darabszám- és időkorlát ott van.
 */
export function forditasGombMod(msg, viewerLang, nowMs = Date.now()) {
  if (!msg || msg.deleted === true) return null;
  if (typeof msg.text !== "string" || msg.text.trim().length === 0) return null;
  if (msg.skipReason === "NO_LETTERS") return null;
  if (!msg.sourceLang || msg.sourceLang === viewerLang) return null;
  const st = msg.translationStatus;
  if (st === "pending" || st === "processing" || st === "retry_wait") {
    // v218: a 15 percnél régebben beragadt "folyamatban" állapotnál újra kérhető a fordítás (a szerver ugyanígy engedi).
    const ms = (ts) => (ts && typeof ts.toMillis === "function" ? ts.toMillis() : ts instanceof Date ? ts.getTime() : null);
    const ido = ms(msg.translationRetryAt) ?? ms(msg.createdAt);
    return ido !== null && nowMs - ido > 15 * 60 * 1000 ? "retry" : null;
  }
  if (st === "error" && msg.errorCode === "SERVICE_PAUSED") return null;
  if (msg.noTranslate === true || msg.skipReason === "NO_TRANSLATE") return "force";
  const translations = msg.translations && typeof msg.translations === "object" ? msg.translations : {};
  const vanForditas = typeof translations[viewerLang] === "string" && translations[viewerLang].length > 0;
  if (st === "skipped") return "force";
  if (st === "done" && !vanForditas) {
    const targets = Array.isArray(msg.targetLangs) ? msg.targetLangs : [];
    return targets.includes(viewerLang) ? "retry" : "force";
  }
  if (st === "done" || st === "error") return "retry";
  return null;
}

/**
 * "Honnan és hogyan készült?" (2026-10-08, tudományos visszakövethetőség): a megjelenített szöveg
 * AI-forrásai soronként - AI-üzenetnél (képjavítás, szövegkinyerés) az AI kimenete, lefordított
 * szövegnél a fordítás (mindkettő is lehet: egy AI-üzenet fordítása). Üres lista, ha nincs mit
 * mutatni (eredeti szöveg, régi üzenet meta nélkül).
 */
export function forrasSorok(msg, kijelzett, uiLang) {
  const ido = (ts) => {
    try {
      const d = ts && typeof ts.toDate === "function" ? ts.toDate() : typeof ts === "number" ? new Date(ts) : null;
      return d ? d.toLocaleString(localeFor(uiLang), { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
    } catch (e) {
      return "";
    }
  };
  const sorok = [];
  const am = msg && msg.aiMeta;
  if (am && am.model) {
    sorok.push(t(uiLang, "message.provenanceAi", { model: am.model, prompt: am.promptVersion || "?", time: ido(am.at) }));
  }
  const tm = msg && msg.translationMeta;
  if (kijelzett && kijelzett.isTranslated && tm && tm.model) {
    sorok.push(t(uiLang, "message.provenanceTranslation", {
      model: tm.model, prompt: tm.promptVersion || "?", glossary: typeof tm.glossaryVersion === "number" ? tm.glossaryVersion : "?", time: ido(tm.at),
    }));
  }
  return sorok;
}

/**
 * A küldés célja a beviteli sáv fölött (2026-10-19, a titkár #35): "Cél: Szoba / Téma" - ugyanabból a szoba- és témaazonosítóból,
 * amellyel a küldés is megy (model.currentRoomId / model.currentTopicId), így szobaváltás után nem maradhat régi cím.
 * @returns {string} üres, ha nincs szoba
 */
export function kuldesiCel(model, uiLang, contentLang) {
  const szoba = (model.rooms || []).find((r) => r && r.roomId === model.currentRoomId);
  if (!szoba) return "";
  let cel = getRoomDisplayName(szoba, contentLang, uiLang);
  if (model.forum === true && model.currentTopicId && model.aktTema && model.aktTema.kind === "discussion") {
    cel += " / " + temaCim(model.aktTema, contentLang);
  }
  return t(uiLang, "chat.sendTarget", { target: cel });
}

export function feladoNev(src, viewerLang, uiLang) {
  if (src && src.senderDeleted === true) return t(uiLang, "msg.deletedSender");
  return (
    pickSenderName(
      {
        senderName: src && src.senderName,
        senderNameLatn: src && src.senderNameLatn,
        senderNameJa: src && src.senderNameJa,
        senderNameZh: src && src.senderNameZh,
        senderNameKo: src && src.senderNameKo,
        senderNameCyr: src && src.senderNameCyr,
        senderNameEl: src && src.senderNameEl,
      },
      viewerLang
    ) || t(uiLang, "msg.unknownSender")
  );
}

function renderMessage(msg, viewerLang, uiLang, myUid, handlers, osszesUzenet, editState) {
  const isOwn = msg.senderId === myUid;
  const es = editState || {};

  const row = el("div", { class: "msg-row " + (isOwn ? "own" : "other") });
  // A görgetéshez és a kiemeléshez az üzenet azonosítója kell.
  if (msg.messageId) row.dataset.messageId = msg.messageId;

  // --- TÖRÖLT ÜZENET (2026-10-02, messageActions.js): sírkő - a hely és a
  // sorrend megmarad, a tartalom (szöveg, kép, idézet) nem. Korai
  // visszatérés: a lenti szerkesztés/kép/idézet logika törölt üzenetnél
  // nem értelmezhető.
  if (msg.deleted === true) {
    // A törölt JELZÉS elrejtése (v151, a titkár #84 javaslata): az elrejtett jelzést a tagok nem látják; az admin halványan látja, és vissza tudja állítani.
    const jelzesRejtett = msg.markerHidden === true;
    const adminLatja = es.isAdmin === true;
    if (jelzesRejtett && !adminLatja) {
      row.hidden = true;
      row.classList.add("msg-marker-hidden");
      return row;
    }
    const meta = el("div", { class: "msg-meta" });
    meta.appendChild(el("span", { class: "sender-name", text: feladoNev(msg, viewerLang, uiLang) }));
    const timeStr = formatTime(msg.createdAt, uiLang);
    if (timeStr) meta.appendChild(el("span", { class: "msg-time", text: timeStr }));
    row.appendChild(meta);
    const bubble = el("div", { class: "bubble msg-deleted" + (jelzesRejtett ? " marker-hidden-admin" : "") });
    bubble.appendChild(el("div", { class: "msg-note", text: t(uiLang, jelzesRejtett ? "message.markerHiddenNote" : "message.deletedPlaceholder") }));
    row.appendChild(bubble);
    if (adminLatja && msg.messageId && handlers && handlers.onHideDeletedMarker) {
      const muvelet = (rejtse) => handlers.onHideDeletedMarker(msg.messageId, rejtse);
      if (jelzesRejtett) {
        row.appendChild(el("button", { type: "button", class: "text-btn msg-marker-restore", text: t(uiLang, "message.markerRestore"), onclick: () => muvelet(false) }));
      } else {
        // "⋯" menü; okostelefonon ugyanez hosszú érintéssel is megnyílik (a "⋯" megmarad azoknak, akik nem ismerik a hosszú érintést).
        const menu = el("div", { class: "msg-marker-menu" }, [
          el("button", { type: "button", class: "text-btn msg-marker-remove", text: t(uiLang, "message.markerRemove"), onclick: () => muvelet(true) }),
        ]);
        menu.hidden = true;
        const gomb = el("button", {
          type: "button", class: "text-btn msg-marker-more", text: "⋯",
          title: t(uiLang, "message.markerMenu"), "aria-label": t(uiLang, "message.markerMenu"),
          onclick: () => { menu.hidden = !menu.hidden; },
        });
        let idozito = null;
        const torol = () => { if (idozito) { clearTimeout(idozito); idozito = null; } };
        bubble.addEventListener("touchstart", () => { torol(); idozito = setTimeout(() => { menu.hidden = false; idozito = null; }, 600); }, { passive: true });
        for (const esemeny of ["touchend", "touchmove", "touchcancel"]) bubble.addEventListener(esemeny, torol, { passive: true });
        row.appendChild(gomb);
        row.appendChild(menu);
      }
    }
    return row;
  }

  const kijelzett = chooseDisplayText(msg, viewerLang, uiLang);
  // Eredeti / fordítás átváltása (2026-10-07, tagi kérésre): a lefordított üzenetnél a tag
  // megnézheti az eredeti szöveget is (és vissza). Csak megjelenítés - nem módosít semmit,
  // és csak az adott tag saját nézetére hat.
  const forditva = kijelzett.isTranslated === true && typeof kijelzett.original === "string";
  const eredetiMutat = forditva && es.showOriginalIds instanceof Set && es.showOriginalIds.has(msg.messageId);
  // Egymás mellett (2026-10-19, a titkár #2/5. javaslata): a lefordított hozzászólásnál az eredeti és a fordítás EGYMÁS MELLETT (keskeny képernyőn
  // egymás alatt) - a tag saját beállítása (minden hozzászólásra), nem módosít semmit. Az "Eredeti" kapcsoló (csak az eredeti) előbbre való.
  const parNezet = forditva && !eredetiMutat && es.egymasMellett === true && typeof kijelzett.original === "string" && kijelzett.original.trim() !== "";
  const text = eredetiMutat ? kijelzett.original : kijelzett.text;
  // A szöveg nyelve (betűforma, felolvasás) az eredetinél a FORRÁSNYELV, nem a néző nyelve.
  const szovegNyelv = eredetiMutat
    ? (typeof msg.sourceLang === "string" && msg.sourceLang ? msg.sourceLang : null)
    : kijelzett.lang;
  const note = eredetiMutat ? t(uiLang, "message.originalShown") : kijelzett.note;

  // --- Fejléc a buborék FÖLÖTT: feladó neve + időbélyeg.
  // MINDIG a küldő tényleges, regisztrált neve jelenik meg - saját
  // üzenetnél is. Így a beszélgetés utólagos olvasásakor, illetve
  // képernyőképen vagy exportban is egyértelmű, ki írta az üzenetet,
  // nem csak az adott felhasználó nézőpontjából.
  const meta = el("div", { class: "msg-meta" });

  // A küldő neve a TARTALOM nyelve (viewerLang) szerinti írásmódban - ha a
  // küldő megadott hozzá alakot. Egyébként az eredeti név marad.
  const senderNameText = feladoNev(msg, viewerLang, uiLang);
  meta.appendChild(el("span", { class: "sender-name", text: senderNameText }));

  const timeStr = formatTime(msg.createdAt, uiLang);
  if (timeStr) {
    meta.appendChild(el("span", { class: "msg-time", text: timeStr }));
    // Fórum (2026-10-09): a hozzászólás sorszáma a témán belül - pontosan lehet rá hivatkozni.
    if (Number.isInteger(msg.postNumber)) meta.appendChild(el("span", { class: "msg-postnum", text: `#${msg.postNumber}` }));
  }
  // "(szerkesztve)" jelzés (2026-10-02): a régi szöveg nem marad megőrizve
  // sehol, csak az a tény, hogy változott - ez szándékos, lásd messageActions.js.
  if (msg.edited === true) {
    meta.appendChild(el("span", { class: "msg-edited-label", text: t(uiLang, "message.editedLabel") }));
  }
  // Válasz gomb. SZÁNDÉKOSAN külön gomb, nem a buborékra kötött kattintás:
  // a buborékban hivatkozás és kép is lehet, és egy véletlen koppintás nem
  // indíthat választ, amiről a felhasználó nem tud.
  // Csak olvasható szobában (2026-10-08) a tag nem válaszolhat - a válasz is üzenetküldés.
  if (msg.messageId && handlers && handlers.onReplyTo && es.irhato !== false) {
    meta.appendChild(
      el("button", {
        type: "button",
        class: "msg-reply-btn",
        text: "↩",
        title: t(uiLang, "reply.action"),
        "aria-label": t(uiLang, "reply.action"),
        onclick: () => handlers.onReplyTo(msg.messageId),
      })
    );
  }
  // Szerkesztés/törlés gomb (2026-10-02): a SAJÁT üzenetet a küldő
  // szerkesztheti és törölheti, egy admin bármelyikét törölheti (de nem
  // szerkesztheti - lásd messageActions.js, "ki teheti" megjegyzés). A
  // pontos időkeretet a szerver kényszeríti ki; itt szándékosan nem
  // számoljuk újra (lásd main.js onStartEdit megjegyzése).
  // 2026-10-07: a szerkesztés 24 óráig, a törlés (a küldőnek) 15 percig él, az adminnak mindig.
  // A gomb csak akkor látszik, ha a szerver el is fogadná (órahibára ráhagyással, lásd
  // messageWindows.js); a döntés továbbra is a szerveré.
  const mostMs = Date.now();
  // Csak olvasható szobában (2026-10-08) a tag a saját régi üzenetét sem szerkesztheti (törölni igen).
  const szerkesztheto = isOwn && szerkesztesiIdonBelul(msg, mostMs) && es.irhato !== false;
  const torolheto = es.isAdmin === true || (isOwn && torlesiIdonBelul(msg, mostMs));
  const valthato = forditva && Boolean(handlers && handlers.onToggleOriginal);
  // Cetli (2026-10-07): privát, eldobható üzenet a küldőnek. Csak MÁS TAG üzenetén: nem a sajátodon, nem az
  // AI-n, nem törölt tag üzenetén (nincs kinek küldeni). A szerver ellenőrzi a közös szobát.
  const cetlizheto = !isOwn && Boolean(msg.senderId) && msg.senderId !== "ai" && msg.senderDeleted !== true &&
    Boolean(handlers && handlers.onOpenNoteComposer);
  // Másolás (2026-10-03, tagi kérésre): BÁRKI üzenetét másolhatja, nem csak
  // a sajátját - ez eltér a szerkesztés/törlés jogosultságától, mert a
  // másolás nem módosít semmit, csak a felhasználó saját vágólapjára tesz
  // egy MÁSOLATOT a már látott szövegről. Csak akkor ajánljuk fel, ha
  // ténylegesen van másolható szöveg (egy felirat nélküli, csak képből álló
  // üzenetnél nincs mit másolni).
  const masolhato = Boolean(text && text.trim().length > 0);
  // Címkék a hozzászólásnál (2026-10-16): a szerző, a téma nyitója, admin / témafelelős jelölheti (a szerver is így dönt).
  const cimkezheto = es.forumTema === true && (es.tagKezelo === true || isOwn) && Boolean(handlers && handlers.onOpenPostTags);
  const kapcsolhato = es.forumTema === true && (es.tagKezelo === true || isOwn) && Boolean(handlers && handlers.onOpenPostLinks) && msg.deleted !== true;
  if (msg.messageId && handlers && (szerkesztheto || torolheto || masolhato || valthato || cetlizheto || cimkezheto || kapcsolhato)) {
    const actions = el("div", { class: "msg-actions" });
    if (valthato) {
      actions.appendChild(
        el("button", {
          type: "button", class: "msg-toggle-btn" + (eredetiMutat ? " active" : ""),
          text: t(uiLang, eredetiMutat ? "message.showTranslation" : "message.showOriginal"),
          "aria-pressed": eredetiMutat ? "true" : "false",
          onclick: () => handlers.onToggleOriginal(msg.messageId),
        })
      );
    }
    // Egymás mellett (2026-10-19): a beállítás minden lefordított hozzászólásra egyszerre érvényes.
    if (valthato && handlers.onTogglePair && !eredetiMutat) {
      actions.appendChild(
        el("button", {
          type: "button", class: "msg-toggle-btn msg-pair-btn" + (parNezet ? " active" : ""),
          text: "⇄ " + t(uiLang, "message.pairView"), title: t(uiLang, "message.pairViewHint"),
          "aria-pressed": parNezet ? "true" : "false",
          onclick: () => handlers.onTogglePair(),
        })
      );
    }
    if (masolhato) {
      const masolasGomb = el("button", {
        type: "button", class: "msg-copy-btn", text: t(uiLang, "message.copy"),
      });
      const alapSzoveg = t(uiLang, "message.copy");
      let visszaallitas = null;
      masolasGomb.addEventListener("click", async () => {
        try {
          await vagolapraMasol(text);
          masolasGomb.textContent = t(uiLang, "message.copied");
          masolasGomb.classList.add("msg-copy-done");
          masolasGomb.classList.remove("msg-copy-error");
        } catch (err) {
          log.error("[üzenet] másolás hiba:", err);
          masolasGomb.textContent = t(uiLang, "message.copyFailed");
          masolasGomb.classList.add("msg-copy-error");
          masolasGomb.classList.remove("msg-copy-done");
        }
        // A VISSZAJELZÉS ideiglenes: pár másodperc után a gomb visszaáll az
        // eredeti feliratára - enélkül a tag nem tudná, hogy a második
        // kattintás is valóban lefutott (a felirat már "Másolva!" lenne).
        if (visszaallitas) clearTimeout(visszaallitas);
        visszaallitas = setTimeout(() => {
          masolasGomb.textContent = alapSzoveg;
          masolasGomb.classList.remove("msg-copy-done", "msg-copy-error");
        }, 1800);
      });
      actions.appendChild(masolasGomb);
    }
    // Hivatkozás a hozzászólásra (2026-10-18): megosztható cím (csak azonosítók); a megnyitáskor a tag tagságát a szerver ellenőrzi.
    if (handlers && handlers.messageLink && msg.messageId && !msg.deleted) {
      const hivGomb = el("button", { type: "button", class: "msg-link-btn", text: "🔗 " + t(uiLang, "message.copyLink") });
      let hivVissza = null;
      hivGomb.addEventListener("click", async () => {
        const alap = "🔗 " + t(uiLang, "message.copyLink");
        try {
          await vagolapraMasol(handlers.messageLink(msg.messageId));
          hivGomb.textContent = t(uiLang, "message.copied");
          hivGomb.classList.add("msg-copy-done");
        } catch (err) {
          log.error("[üzenet] hivatkozás másolása:", err);
          hivGomb.textContent = t(uiLang, "message.copyFailed");
          hivGomb.classList.add("msg-copy-error");
        }
        if (hivVissza) clearTimeout(hivVissza);
        hivVissza = setTimeout(() => { hivGomb.textContent = alap; hivGomb.classList.remove("msg-copy-done", "msg-copy-error"); }, 1800);
      });
      actions.appendChild(hivGomb);
    }
    if (cimkezheto) {
      actions.appendChild(
        el("button", {
          type: "button", class: "msg-tag-btn", text: "🔖 " + t(uiLang, "forum.tagsEdit"),
          onclick: () => handlers.onOpenPostTags(msg.messageId),
        })
      );
    }
    if (kapcsolhato) {
      actions.appendChild(
        el("button", {
          type: "button", class: "msg-links-btn", text: "⛓ " + t(uiLang, "message.linksBtn"),
          onclick: () => handlers.onOpenPostLinks(msg.messageId),
        })
      );
    }
    if (cetlizheto) {
      actions.appendChild(
        el("button", {
          type: "button", class: "msg-note-btn", text: t(uiLang, "note.action"),
          title: t(uiLang, "note.actionHint"),
          onclick: () => handlers.onOpenNoteComposer(msg.messageId),
        })
      );
    }
    if (szerkesztheto && handlers.onStartEdit) {
      actions.appendChild(
        el("button", {
          type: "button", class: "msg-edit-btn", text: t(uiLang, "message.edit"),
          onclick: () => handlers.onStartEdit(msg.messageId),
        })
      );
    }
    if (torolheto && handlers.onDeleteMessage) {
      actions.appendChild(
        el("button", {
          type: "button", class: "msg-delete-btn", text: t(uiLang, "message.delete"),
          onclick: () => handlers.onDeleteMessage(msg.messageId),
        })
      );
    }
    meta.appendChild(actions);
  }
  row.appendChild(meta);

  // --- A buborék: kizárólag az üzenet szövege és a státuszjelzés.
  const bubble = el("div", { class: "bubble" });

  // --- SZERKESZTŐ MEZŐ (2026-10-02): ha ÉPP ezt az üzenetet szerkeszti a
  // felhasználó, a szöveg/jegyzet helyén egy textarea + Mentés/Mégse
  // jelenik meg. A kép és az idézet VÁLTOZATLANUL látszik felette - a v1
  // csak a szöveget engedi szerkeszteni, a képet nem (lásd messageActions.js
  // fejléce: "amit ez (még) nem kezel").
  const szerkesztesAlatt = es.editingMessageId === msg.messageId;
  // A szerkesztő mező a FELÜLETHEZ igazodik, nem a buborék szűk méretéhez
  // (tagi kérésre, 2026-10-03) - lásd a megfelelő CSS-szabályt
  // (.msg-row.editing .bubble) az index.html-ben.
  if (szerkesztesAlatt) row.classList.add("editing");

  // --- Idézet: melyik üzenetre válasz ez?
  //
  // A megjelenített szöveg forrása két helyről jöhet, ebben a sorrendben:
  //   1. az EREDETI üzenet, ha még a betöltött ablakban van - így az
  //      idézet is az olvasó saját nyelvén jelenik meg, a fordításokkal,
  //   2. a küldéskor elmentett részlet, ha az eredeti már kigörgött.
  if (msg.replyTo && msg.replyTo.messageId) {
    const r = msg.replyTo;
    const eredeti = Array.isArray(osszesUzenet)
      ? osszesUzenet.find((m) => m.messageId === r.messageId)
      : null;

    let idezetSzoveg;
    let idezetNyelv = null;
    if (eredeti) {
      const v = chooseDisplayText(eredeti, viewerLang, uiLang);
      idezetSzoveg = v.text || "";
      idezetNyelv = idezetSzoveg ? v.lang : null;
      if (!idezetSzoveg && eredeti.image && (eredeti.image.path || eredeti.image.url)) idezetSzoveg = t(uiLang, "reply.image");
      if (!idezetSzoveg && eredeti.file && (eredeti.file.path || eredeti.file.url)) idezetSzoveg = t(uiLang, "reply.file");
    } else {
      idezetSzoveg = r.excerpt || (r.hasImage ? t(uiLang, "reply.image") : r.hasFile ? t(uiLang, "reply.file") : "");
    }
    // Az idézett üzenetet időközben törölhették (2026-10-02,
    // messageActions.js) - ilyenkor ugyanazt a "törölve" szöveget mutatjuk,
    // amit a törölt üzenet saját buborékja is kapna, a régi szövegrészlet
    // helyett (az ne maradjon látható egy már törölt üzenetből).
    if (r.messageDeleted === true || (eredeti && eredeti.deleted === true)) {
      idezetSzoveg = t(uiLang, "message.deletedPlaceholder");
    } else if (!idezetSzoveg) {
      idezetSzoveg = t(uiLang, "reply.unavailable");
    }

    const idezet = el("button", {
      type: "button",
      class: "msg-quote",
      title: t(uiLang, "reply.action"),
      onclick: () => handlers && handlers.onJumpToMessage && handlers.onJumpToMessage(r.messageId),
    });
    idezet.appendChild(
      el("span", {
        class: "msg-quote-name",
        text: feladoNev(r, viewerLang, uiLang),
      })
    );
    // v216: az idézet-részlet egyszerű szöveg (nem jelenít meg Markdownt): a ***Név*** csillagai nélkül jelenik meg.
    idezet.appendChild(el("span", { class: "msg-quote-text", lang: idezetNyelv, text: idezetSzoveg.replace(/[*`]+/g, "") }));
    bubble.appendChild(idezet);
  }

  // --- Csatolt kép
  //
  // A képet a szöveg ELÉ tesszük: a csevegésben a kép a fő tartalom, a
  // szöveg a hozzá tartozó megjegyzés.
  //
  // A width/height attribútumot azért adjuk meg, hogy a böngésző még a
  // kép letöltése ELŐTT tudja, mekkora helyet hagyjon - enélkül a lista
  // megugrana minden egyes kép betöltődésekor.
  //
  // Csak https:// cím jelenhet meg. A szerver (sendMessage validateImage)
  // ennél jóval szigorúbb, de a cím egy <a href>-be kerül: egy régebbi
  // vagy kézzel írt adatból érkező "javascript:" cím kattintásra kódot
  // futtatna. Ez a sor az utolsó védvonal (AUDIT, 2026-09-26).
  // TÖBB KÉP (v211): két vagy több képnél az `images` lista minden képe megjelenik (rácsban); egy képnél az `image`.
  const kepLista = Array.isArray(msg.images) && msg.images.length > 1 ? msg.images : (msg.image ? [msg.image] : []);
  const kepElemek = kepLista.map((adat) => kepKapcsolo(adat, uiLang)).filter(Boolean);
  if (kepElemek.length === 1) {
    bubble.appendChild(kepElemek[0]);
  } else if (kepElemek.length > 1) {
    const racs = el("div", { class: `msg-images msg-images-${kepElemek.length}` });
    for (const e of kepElemek) racs.appendChild(e);
    bubble.appendChild(racs);
  }

  // A törölt tag képe törlődött (lásd functions/src/lib/userPurge.js): egy
  // csak képből álló üzenet üres buborék helyett jelzést kap.
  // PDF-melléklet (2026-10-10, tagi kérés: kis PDF-ek, pl. számlák): kártya a megnyitáshoz (új lapon).
  const fajl = fajlHivatkozas(msg);
  if (fajl) {
    const kartya = el("a", { class: "msg-file", href: fajl.url, target: "_blank", rel: "noopener noreferrer", title: t(uiLang, "file.open") });
    kartya.appendChild(el("span", { class: "msg-file-icon", text: "📄" }));
    kartya.appendChild(el("span", { class: "msg-file-name", text: fajl.name }));
    if (fajl.size) kartya.appendChild(el("span", { class: "msg-file-size", text: fajlMeret(fajl.size) }));
    bubble.appendChild(kartya);
  }
  if (msg.fileRemoved === true && !msg.file) {
    bubble.appendChild(el("div", { class: "msg-note", text: t(uiLang, "msg.fileRemoved") }));
  }

  if (msg.imageRemoved === true && !msg.image) {
    bubble.appendChild(el("div", { class: "msg-note", text: t(uiLang, "msg.imageRemoved") }));
  }

  if (szerkesztesAlatt) {
    const szerkeszto = el("div", { class: "msg-edit-form" });
    const area = el("textarea", { class: "msg-edit-textarea", disabled: es.editingBusy === true });
    area.value = es.editingText || "";
    area.addEventListener("input", () => handlers.onEditTextChange && handlers.onEditTextChange(area.value));
    szerkeszto.appendChild(area);

    if (es.editingErrorText || es.editingErrorKey) {
      // A szerver KONKRÉT, biztonsággal mutatható szövege elsőbbséget élvez
      // az elvont felirattal szemben (2026-10-03) - lásd main.js
      // ismertUzenetMuveletHiba().
      szerkeszto.appendChild(el("div", { class: "msg-note warn", text: es.editingErrorText || t(uiLang, es.editingErrorKey) }));
    }

    const gombsor = el("div", { class: "msg-edit-actions" });
    gombsor.appendChild(
      el("button", {
        type: "button", class: "msg-edit-save", disabled: es.editingBusy === true,
        text: t(uiLang, "message.editSave"), onclick: () => handlers.onSaveEdit && handlers.onSaveEdit(),
      })
    );
    gombsor.appendChild(
      el("button", {
        type: "button", class: "msg-edit-cancel", disabled: es.editingBusy === true,
        text: t(uiLang, "message.editCancel"), onclick: () => handlers.onCancelEdit && handlers.onCancelEdit(),
      })
    );
    szerkeszto.appendChild(gombsor);
    bubble.appendChild(szerkeszto);
  } else {
    // A szöveg csak akkor, ha van - egy csak képet tartalmazó üzenetnél
    // egy üres sor csúnya rést hagyna a buborékban.
    if (text && text.trim().length > 0) {
      // Markdown-megjelenítés (2026-10-04, markdown.js): a szöveg DOM-
      // csomópontokból épül, nem innerHTML-ből - egy üzenet nem tud HTML-t
      // beinjektálni. A másolás gomb a NYERS (jelölős) szöveget másolja.
      const szovegElem = el("div", { class: "msg-text md", lang: szovegNyelv });
      for (const n of markdownCsomopontok(text)) szovegElem.appendChild(n);
      if (parNezet) {
        // Két oszlop: bal az eredeti (a forrásnyelven), jobb a fordítás (a néző nyelvén); mindkettő címkével, hogy tudja, melyik melyik.
        const forrasNyelv = typeof msg.sourceLang === "string" ? msg.sourceLang : "";
        const eredetiElem = el("div", { class: "msg-text md", lang: forrasNyelv || null });
        for (const n of markdownCsomopontok(kijelzett.original)) eredetiElem.appendChild(n);
        const par = el("div", { class: "msg-pair" }, [
          el("div", { class: "msg-pair-col msg-pair-original" }, [el("div", { class: "msg-pair-label", text: t(uiLang, "message.pairOriginal", { lang: forrasNyelv.toUpperCase() || "?" }) }), eredetiElem]),
          el("div", { class: "msg-pair-col msg-pair-translation" }, [el("div", { class: "msg-pair-label", text: t(uiLang, "message.pairTranslation", { lang: String(szovegNyelv || "").toUpperCase() || "?" }) }), szovegElem]),
        ]);
        bubble.appendChild(par);
      } else bubble.appendChild(szovegElem);
    }

    if (note) {
      bubble.appendChild(el("div", { class: "msg-note", text: note }));
    }
    // AI-jelvény (2026-10-19, a titkár A/7 kérése): a képjavítás és a szövegkinyerés eredménye látható jelölést kap.
    const aiJelvenyKulcs = msg.aiMeta && msg.aiMeta.kind === "image_restore" ? "message.aiBadgeRestore" : msg.aiMeta && msg.aiMeta.kind === "ocr" ? "message.aiBadgeOcr" : msg.aiMeta && msg.aiMeta.kind === "modernize" ? "message.aiBadgeModern" : null;
    if (aiJelvenyKulcs) bubble.appendChild(el("div", { class: "msg-ai-badge", text: "✨ " + t(uiLang, aiJelvenyKulcs) }));
    // Az AI-kérés ("@1" / "@2") állapota a kérést tartalmazó hozzászólás alatt (v201, tagi kérésre): folyamatban / kész / hiba.
    const aiAllapot = msg.aiStatus && ["pending", "processing", "done", "error"].includes(msg.aiStatus.state) ? msg.aiStatus : null;
    if (aiAllapot) {
      const jel = { pending: "⏳", processing: "⏳", done: "✔", error: "⚠" }[aiAllapot.state];
      bubble.appendChild(el("div", {
        class: "msg-ai-status msg-ai-status-" + aiAllapot.state, role: "status",
        text: jel + " " + t(uiLang, "message.aiStatus." + aiAllapot.state, { cmd: aiAllapot.parancs === "@2" ? "@2" : "@1" }),
      }));
    }
    // Honnan és hogyan készült (2026-10-08): becsukható sor - alapból csak egy kis ⓘ.
    // Az eredeti szöveg nézetében a fordítás forrása nem tartozik a látott szöveghez.
    const forras = forrasSorok(msg, eredetiMutat ? { isTranslated: false } : kijelzett, uiLang);
    if (forras.length > 0) {
      const reszletek = el("details", { class: "msg-provenance" });
      reszletek.appendChild(el("summary", { text: "ⓘ", title: t(uiLang, "message.provenanceTitle"), "aria-label": t(uiLang, "message.provenanceTitle") }));
      for (const sor of forras) reszletek.appendChild(el("div", { class: "msg-provenance-text", text: sor }));
      bubble.appendChild(reszletek);
    }
    // Ellenőrzési pecsét (2026-10-18): a megjelenített nyelvű szövegre; elavulását a szövegek összevetése mutatja.
    const pecsetSor = es.forumTema === true ? renderPecsetek(msg, szovegNyelv, text, uiLang, myUid, es, handlers) : null;
    if (pecsetSor) bubble.appendChild(pecsetSor);
    // Fordítás / újrafordítás gomb a poszt alatt (2026-10-08, tagi kérésre; korábban csak hibánál):
    //   "force" - kényszerített fordítás ("#" jellel küldött, vagy a néző nyelvére nem fordított üzenet),
    //   "retry" - újrafordítás (kész vagy hibás fordítás).
    // Az eredeti szöveg nézetében nincs (ott a néző épp az eredetit kérte).
    const gombMod = forditasGombMod(msg, viewerLang);
    if (!eredetiMutat && gombMod && handlers && handlers.onRetryTranslation) {
      const foglalt = es.retryBusyIds instanceof Set && es.retryBusyIds.has(msg.messageId);
      bubble.appendChild(
        el("button", {
          type: "button", class: "msg-retry-btn", disabled: foglalt,
          text: t(uiLang, foglalt ? "message.retrying" : gombMod === "force" ? "message.forceTranslate" : "message.retryTranslation"),
          onclick: () => handlers.onRetryTranslation(msg.messageId),
        })
      );
    }
  }

  row.appendChild(bubble);
  const cimkeSor = es.forumTema === true ? renderPostTags(msg, es, viewerLang, uiLang, handlers) : null;
  if (cimkeSor) row.appendChild(cimkeSor);
  const kapcsSor = es.forumTema === true ? renderPostLinks(msg, es, uiLang, handlers) : null;
  if (kapcsSor) row.appendChild(kapcsSor);
  // Fordításjavaslat (2026-10-19): csak a megjelenített FORDÍTÁSON (az eredeti nézetében nincs).
  const fordNyelv = !eredetiMutat && kijelzett && kijelzett.isTranslated === true ? szovegNyelv : null;
  const javaslatSor = renderFordJavaslat(msg, fordNyelv, uiLang, myUid, es, handlers);
  if (javaslatSor) row.appendChild(javaslatSor);
  // Átirat (2026-10-19): jelvény, "javítva" jelzés, javító szerkesztő, előzmény.
  const atiratSor = renderAtirat(msg, viewerLang, uiLang, myUid, es, handlers);
  if (atiratSor) row.appendChild(atiratSor);
  // Karakterszavazó (2026-10-30): a Paleo szavazó üzenete alatt a kattintásos szavazó.
  const szavazoDoboz = renderSzavazo(msg, uiLang, es, handlers);
  if (szavazoDoboz) row.appendChild(szavazoDoboz);
  // Automatikus feltöltés (v123, a titkár #57 kérése): a külső program (Paleo) által feltöltött hozzászólás jelvényt kap, hogy ne legyen összetéveszthető a szerző saját írásával.
  if (msg.migratedFrom && typeof msg.migratedFrom === "object" && msg.migratedFrom.tool === "paleo") {
    row.appendChild(el("div", { class: "msg-import-badge", text: "⚙ " + t(uiLang, "message.autoImport", { tool: "Paleo" }) }));
  }
  // v220 (#108): egy ujjas válasz telefonon - a hozzászólást jobbra húzva ugyanaz történik, mint a ↩ gomb megnyomásakor.
  if (msg.messageId && handlers && handlers.onReplyTo && es.irhato !== false) {
    swipeValaszKotese(row, () => handlers.onReplyTo(msg.messageId));
  }
  return row;
}

/** A húzás eredménye: "valasz" (elég hosszú, főleg vízszintes jobbra húzás), "fuggoleges" (görgetés), különben "kevés". Tiszta függvény (tesztelhető). */
export function swipeEredmeny(dx, dy, kuszob = 70) {
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  if (ay > 28 && ay >= ax * 0.6) return "fuggoleges";
  if (dx >= kuszob && ax >= ay * 2) return "valasz";
  return "kevés";
}

/** Jobbra húzás = válasz (csak érintőképernyőn; a hivatkozásra, gombra, beviteli mezőre induló érintést nem figyeli). Nem blokkolja a görgetést. */
export function swipeValaszKotese(row, onReply) {
  if (!row || typeof row.addEventListener !== "function") return;
  let x0 = 0;
  let y0 = 0;
  let aktiv = false;
  const vissza = () => {
    row.style.transform = "";
    row.classList.remove("swipe-reply-ready");
  };
  row.addEventListener("touchstart", (e) => {
    const t0 = e.touches && e.touches.length === 1 ? e.touches[0] : null;
    const cel = e.target && e.target.closest ? e.target.closest("a, button, input, textarea, select, img, [contenteditable]") : null;
    aktiv = Boolean(t0) && !cel;
    if (aktiv) { x0 = t0.clientX; y0 = t0.clientY; }
  }, { passive: true });
  row.addEventListener("touchmove", (e) => {
    if (!aktiv || !e.touches || e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - x0;
    const dy = e.touches[0].clientY - y0;
    const er = swipeEredmeny(dx, dy);
    if (er === "fuggoleges") { aktiv = false; vissza(); return; }
    if (dx > 8) {
      row.style.transform = `translateX(${Math.min(dx, 90) * 0.5}px)`;
      row.classList.toggle("swipe-reply-ready", er === "valasz");
    }
  }, { passive: true });
  row.addEventListener("touchend", (e) => {
    if (!aktiv) { vissza(); return; }
    aktiv = false;
    const t1 = e.changedTouches && e.changedTouches[0];
    const er = t1 ? swipeEredmeny(t1.clientX - x0, t1.clientY - y0) : "kevés";
    vissza();
    if (er === "valasz") onReply();
  }, { passive: true });
  row.addEventListener("touchcancel", () => { aktiv = false; vissza(); }, { passive: true });
}

/**
 * A teljes chat képernyő kirajzolása.
 * A megjelenítés minden rendereléskor teljes: a lista kicsi (max 50),
 * így ez egyszerűbb és megbízhatóbb, mint a részleges frissítés.
 */
// ============================================================================
// A csevegő képernyő RÉSZLEGES frissítése.
//
// FONTOS, VALÓS HIBÁBÓL TANULVA: korábban minden állapotváltozás az egész
// képernyőt újraépítette (app.innerHTML = ""), a beviteli mezővel együtt.
// Mobilon ennek két kellemetlen következménye volt:
//
//   1. Gépelés közben érkező üzenet MEGSEMMISÍTETTE a textarea elemet, és
//      egy újat tett a helyére. A fókusz elveszett, a kurzor a szöveg
//      végére ugrott, a billentyűzet becsukódott vagy odébb csúszott -
//      japán/kínai bevitelnél ráadásul a félkész átalakítás is megszakadt.
//
//   2. Az újraépítés után a lista aljára görgettünk, de a beviteli mező
//      magasságát CSAK EZUTÁN igazítottuk. A magasságváltozás elmozdította
//      a lista alját, így a frissen elküldött üzenet a látható rész alá
//      került - a felhasználó nem látta, hogy elment.
//
// Ezért a képernyőnek most állandó váza van: a beviteli sáv EGYSZER jön
// létre, és soha nem kerül ki a DOM-ból. Csak a változó részek (fejléc,
// üzenetlista, hibasáv, képelőnézet) épülnek újra.
// ============================================================================

// A megtartott váz. A modul szintjén él, mert a renderek között megmarad.
let mounted = null;
// A váz `visualViewport` "resize" figyelője (billentyűzet): a váz újraépülésekor ezt kell leszedni.
let viewportFigyelo = null;

const HORGONY_IDO_MS = 2000;

/**
 * A görgetési horgony (v188): a legutóbb kattintott sor (ha a kattintás frissebb 2 mp-nél), különben a lista tetején látható első sor, és a
 * lista tetejéhez mért helye. A DOM-módosítás ELŐTT kell hívni.
 * @returns {{id:string, eltolas:number}|null}
 */
function horgonyMegjegyzese(m) {
  const listaTop = m.list.getBoundingClientRect().top;
  let sor = null;
  if (m.kattHorgony && Date.now() - m.kattHorgony.ido < HORGONY_IDO_MS) {
    sor = [...m.list.children].find((x) => x.dataset && x.dataset.messageId === m.kattHorgony.id) || null;
  }
  if (!sor) sor = [...m.list.children].find((x) => x.dataset && x.dataset.messageId && x.getBoundingClientRect().bottom > listaTop + 1) || null;
  return sor ? { id: sor.dataset.messageId, eltolas: sor.getBoundingClientRect().top - listaTop } : null;
}

/** A horgony sora ugyanoda kerül a lista tetejéhez képest, ahol volt (a többi sor magassága közben megváltozhatott). */
function horgonyVisszaallitasa(m, h) {
  const sor = [...m.list.children].find((x) => x.dataset && x.dataset.messageId === h.id);
  if (!sor) return;
  const uj = sor.getBoundingClientRect().top - m.list.getBoundingClientRect().top;
  const kulonbseg = uj - h.eltolas;
  if (Math.abs(kulonbseg) >= 1) m.list.scrollTop += kulonbseg;
}

/** Az aljára van-e görgetve a lista (pár képpont tűréssel)? */
function vanLegaljan(list) {
  return list.scrollHeight - list.scrollTop - list.clientHeight < 40;
}

export function renderChatScreen(app, model, handlers) {
  const ujVaz =
    !mounted ||
    mounted.app !== app ||
    !mounted.top.isConnected ||
    mounted.top.parentNode !== app;

  if (ujVaz) {
    app.innerHTML = "";
    mounted = buildSkeleton(app, handlers);
  } else {
    // Az előző render átfedő paneljei (szobaváltó, profil, admin) nem
    // részei a váznak. Korábban ezeket az innerHTML törlése takarította
    // el; most nekünk kell, különben egymásra halmozódnának.
    for (const node of [...app.children]) {
      if (!mounted.owned.has(node)) node.remove();
    }
  }

  app.className = "chat-layout";
  iroSavAlkalmaz(mounted, mounted.iroKinyitva === true); // a className-csere nem törölheti az írósáv összecsukott jelzését (v183)
  updateChatScreen(model, handlers, ujVaz);
  return { inputEl: mounted.input, listEl: mounted.list };
}

/**
 * Az állandó váz felépítése. Csak egyszer fut - a benne létrehozott
 * beviteli sáv és a rá akasztott eseménykezelők végig élnek.
 *
 * A kezelők (handlers) a main.js modul szintű, ÁLLANDÓ objektuma, ezért
 * biztonságos egyszer beakasztani őket: mindig a friss állapoton
 * dolgoznak.
 */
function buildSkeleton(app, handlers) {
  const top = el("div", { class: "chat-top" });
  const list = el("div", { class: "messages", id: "messages" });
  const errorSlot = el("div", { class: "chat-error-slot" });
  const previewSlot = el("div", { class: "image-preview-slot" });
  const replySlot = el("div", { class: "reply-slot" });

  // --- Beviteli sáv (ÁLLANDÓ - soha nem épül újra)
  const bar = el("div", { class: "input-bar is-collapsed" });
  // ÖSSZECSUKHATÓ ÍRÓSÁV (v183, Tamás: "minden alszobánál, minden szobában és minden felületen legyen összecsukva"): alapból egyetlen vékony sor
  // ("✎ Írj üzenetet…"), hogy olvasáskor a tartalomé legyen a hely; koppintásra kinyílik a teljes szerkesztő. Összecsukja: szoba- / témaváltás,
  // sikeres küldés, a "▾" gomb. Kinyitva marad: válasz, csatolt kép / PDF, átirat, küldés közben. A szerkesztő elemei (mező, gombok) megmaradnak.
  const iroZar = el("button", { type: "button", class: "input-collapse-btn", text: "▾" });
  const iroMini = el("button", { type: "button", class: "input-mini" });
  bar.appendChild(iroZar);
  bar.appendChild(iroMini);

  // Képcsatolás gomb. A rejtett fájlválasztó a szabványos megoldás:
  // a natív <input type="file"> kinézete nem formázható, ezért egy saját
  // gomb indítja el.
  const fileInput = el("input", {
    type: "file",
    // A szűrő SZÁNDÉKOSAN tág (image/*): a szűk típuslistától egyes
    // telefonok galériája nem ajánlja fel a fotókat, és elmarad a
    // HEIC→JPEG automatikus átalakítás is. A valódi ellenőrzés a
    // fájl bájtjai alapján történik (imageUpload.js).
    accept: ACCEPTED_TYPES,
    id: "image-input",
    // Több kép egy bejegyzésben (v211): egyszerre több kép is kijelölhető (legfeljebb 4 kerül a bejegyzésre).
    multiple: true,
  });
  fileInput.style.display = "none";
  fileInput.addEventListener("change", async () => {
    let fajlok = Array.from(fileInput.files || []);
    // Több fájlnál a PDF-et kihagyjuk: egy üzenethez kép VAGY PDF tartozhat; egyetlen fájlnál a régi út marad.
    if (fajlok.length > 1) fajlok = fajlok.filter((f) => f.type !== "application/pdf").slice(0, MAX_KEPEK);
    // Nullázás: enélkül ugyanazt a fájlt másodszor kiválasztva nem sülne el a change esemény.
    fileInput.value = "";
    for (const file of fajlok) await handlers.onImageSelected(file);
  });
  bar.appendChild(fileInput);

  const attachBtn = el("button", {
    type: "button",
    class: "attach-btn",
    text: "＋",
    onclick: () => fileInput.click(),
  });
  bar.appendChild(attachBtn);

  const input = el("textarea", { id: "message-input", rows: "3" });

  // A mező gépelés közben nő, de a képernyő 40%-ánál nem tovább - így
  // hosszú üzenetnél is marad látható hely a beszélgetésnek.
  const maxInputHeight = () => Math.round(window.innerHeight * 0.4);
  const autoGrow = () => {
    input.style.height = "auto";
    input.style.height = Math.min(input.scrollHeight, maxInputHeight()) + "px";
  };

  // --- A beviteli mező és az állapot (draft) szinkronja
  //
  // FONTOS, VALÓS HIBÁBÓL TANULVA (2026-09-26, japán tag jelezte): gépelés
  // közben a szöveg néha kétszer-háromszor jelent meg, egy pillanatnyi
  // megakadás után. Az ok: minden render (beérkező üzenet, a fordítás
  // állapotváltozásai - üzenetenként 2-3 is) összevetette a mező értékét az
  // állapottal, és ELTÉRÉS esetén beírta a mezőbe. Japán (IME) bevitelnél a
  // félkész, még át nem alakított szöveg (kompozíció) alatt a mező értéke és
  // a legutóbb jelentett érték eltérhet; a programból beírt érték ilyenkor
  // megszakítja a kompozíciót, a billentyűzet pedig a pufferét ÚJRA
  // beszúrja - rendenként egyszer. Innen a két-három ismétlés.
  //
  // A szabály ezért:
  //   1. A mezőbe csak a PROGRAM saját változtatását írjuk (küldés utáni
  //      ürítés, részleges küldés maradéka) - azt ismerjük fel abból, hogy
  //      a draft eltér attól, amit a mező legutóbb maga jelentett. A mező
  //      saját tartalmát soha nem írjuk vissza önmagába.
  //   2. Kompozíció KÖZBEN semmit nem írunk; a függő változtatás a
  //      kompozíció végén kerül be.
  const bevitel = { komponal: false, jelentett: "", fuggo: null };
  // A súgósor "több részletben megy el" jelzése GÉPELÉS KÖZBEN (2026-10-22, kódaudit): a gépelés nem rajzol újra (onDraftChange), ezért a súgósor eddig csak
  // egy következő renderkor frissült - a jelzés a küldésig nem látszott. A mostani súgószöveget, a darabolási korlátot és a nyelvet az updateChatScreen
  // jegyzi ide; az input-figyelő ebből frissíti a sort azonnal (csak akkor, ha a darabszám megváltozott).
  const hintKontextus = { alap: null, korlat: 0, lang: "hu", darab: 1 };

  const frissitsdADarabolasiHintet = () => {
    if (hintKontextus.alap === null || !hintKontextus.korlat) return;
    const ertek = input.value;
    // rövid szövegnél nincs darabolás (olcsó kapu: a splitMessage csak a korlát fölött fut)
    const darab = ertek.length > hintKontextus.korlat && ertek.trim() ? splitMessage(ertek, hintKontextus.korlat).length : 1;
    if (darab === hintKontextus.darab) return;
    hintKontextus.darab = darab;
    hint.textContent = darab > 1 ? t(hintKontextus.lang, "chat.tooLongSplit", { count: darab }) : hintKontextus.alap;
  };

  input.addEventListener("input", () => {
    bevitel.jelentett = input.value;
    // Ha kompozíció közben a program már átírta az állapotot (pl. az
    // üzenet elment, a mezőt üríteni kell), a félkész szöveg NEM írhatja
    // vissza az állapotba - különben a mező a kompozíció végén kiürülne,
    // az állapotban viszont ott maradna az elküldött szöveg, és a
    // következő küldés MÉGEGYSZER elküldené.
    if (bevitel.fuggo === null) handlers.onDraftChange(input.value);
    frissitsdADarabolasiHintet();
    autoGrow();
  });
  input.addEventListener("compositionstart", () => {
    bevitel.komponal = true;
  });
  const kompozicioVege = () => {
    if (!bevitel.komponal) return;
    bevitel.komponal = false;
    if (bevitel.fuggo !== null) {
      // Kompozíció közben a program üríteni vagy átírni akarta a mezőt
      // (pl. közben elment az üzenet) - most már biztonságos.
      const v = bevitel.fuggo;
      bevitel.fuggo = null;
      input.value = v;
      bevitel.jelentett = v;
      autoGrow();
      return;
    }
    // Nem minden böngésző küld "input" eseményt a kompozíció lezárása után;
    // a végleges szöveg így sem veszhet el az állapotból.
    if (input.value !== bevitel.jelentett) {
      bevitel.jelentett = input.value;
      handlers.onDraftChange(input.value);
      autoGrow();
    }
  };
  input.addEventListener("compositionend", kompozicioVege);
  // Ha a böngésző a kompozíció-vége eseményt elhagyná (fókuszvesztés), a
  // mező ne ragadjon örökre "kompozíció alatt" állapotban.
  input.addEventListener("blur", kompozicioVege);
  // --- Az Enter ÚJ SORT szúr be, nem küld.
  //
  // SZÁNDÉKOSAN nincs keydown-kezelő a beviteli mezőn: az Enter a textarea
  // alapértelmezett viselkedését kapja, vagyis sortörést. Az üzenetet
  // kizárólag a küldés gomb küldi el.
  //
  // Ez egyben megszünteti a japán/kínai bevitel (IME) régi buktatóját is:
  // ott az Enter a jelöltlistából való választást erősíti meg, és korábban
  // emiatt ment el félkész üzenet. Mivel most az Enter soha nem küld, erre
  // külön IME-védelem sem kell.
  //
  // Ha valaha visszakerülne az "Enter küld" viselkedés, az IME-védelmet
  // (e.isComposing / keyCode 229 és a composition események) VELE EGYÜTT
  // vissza kell tenni.
  bar.appendChild(input);

  const sendBtn = el("button", {
    type: "button",
    class: "send-btn",
    onclick: handlers.onSend,
  });
  bar.appendChild(sendBtn);

  const hint = el("div", { class: "input-hint" });
  // Küldési cél (2026-10-19, a titkár #35): a beviteli sáv fölött, hogy hosszú olvasás után is lássa, hová megy a hozzászólás.
  const cel = el("div", { class: "input-target", "aria-live": "polite" });
  cel.style.display = "none";

  app.appendChild(top);
  // Fórum (2026-10-09): a témalista helye - fórum-módú szobában, megnyitott téma nélkül ez látszik
  // az üzenetlista és a beviteli sáv helyett.
  const forumPanel = el("div", { class: "forum-panel" });
  forumPanel.style.display = "none";
  app.appendChild(forumPanel);
  app.appendChild(list);
  app.appendChild(errorSlot);
  app.appendChild(previewSlot);
  app.appendChild(replySlot);
  // Átirat (2026-10-19): a kapcsoló + nyelvválasztó sora a beviteli sáv fölött; csak a változásakor épül újra (atiratKulcs).
  const atiratSlot = el("div", { class: "atirat-slot" });
  atiratSlot.style.display = "none";
  app.appendChild(cel);
  app.appendChild(atiratSlot);
  app.appendChild(bar);
  app.appendChild(hint);

  // A mobil billentyűzet megjelenése MEGVÁLTOZTATJA a látható terület
  // magasságát: a lista összemegy, és ami eddig az alján volt, kicsúszik
  // a képernyőről. Ilyenkor visszagörgetünk - de csak ha a felhasználó
  // amúgy is az alján állt, hogy a történetben olvasgatót ne rántsuk el.
  //
  // A visualViewport a billentyűzetre is reagál, az ablak "resize"
  // eseménye Androidon nem mindig.
  const nezet = typeof window !== "undefined" ? window.visualViewport : null;
  if (nezet && typeof nezet.addEventListener === "function") {
    // A váz újraépülhet (kijelentkezés / bejelentkezés, megszakadt kapcsolat után): az előző figyelőt leszedjük,
    // különben felhalmozódnának, és a régi, már leválasztott lista DOM-ját is életben tartanák (audit, 2026-10-15).
    if (viewportFigyelo) nezet.removeEventListener("resize", viewportFigyelo);
    viewportFigyelo = () => {
      if (vanLegaljan(list)) gorgessLegalulra(list);
    };
    nezet.addEventListener("resize", viewportFigyelo);
  }

  const vaz = {
    app, top, list, errorSlot, previewSlot, replySlot, cel, atiratSlot, bar, hint, forumPanel,
    input, fileInput, attachBtn, sendBtn, autoGrow, bevitel, hintKontextus, iroZar, iroMini,
    atiratKulcs: null,
    // Az írósáv összecsukott / nyitott állapota (v183): iroNyitva = a tag nyitotta ki; iroHely = melyik szobához / témához tartozik.
    iroNyitva: false, iroHely: null, elozoKuldes: false,
    // A vázhoz tartozó elemek - ami nem ebben van, az idegen (átfedő panel).
    owned: new Set([top, forumPanel, list, errorSlot, previewSlot, replySlot, cel, atiratSlot, bar, hint]),
    // A görgetési döntéshez: melyik szobát és melyik utolsó üzenetet láttuk.
    lastRoomId: null,
    lastMessageId: null,
  };
  // A kattintott sor megjegyzése a görgetési horgonyhoz (v188): a gomb kezelője ELŐTT fut (rögzítő fázis), így a sor helye még a régi.
  list.addEventListener("click", (e) => {
    const sor = e.target && e.target.closest ? e.target.closest(".msg-row") : null;
    if (sor && sor.dataset && sor.dataset.messageId) vaz.kattHorgony = { id: sor.dataset.messageId, ido: Date.now() };
  }, true);
  // A kinyitás / összecsukás: a mini sor koppintása kinyit és a mezőre ugrik (felhasználói gesztus: a billentyűzet felugorhat); a "▾" összecsuk.
  iroMini.addEventListener("click", () => {
    vaz.iroNyitva = true;
    iroSavAlkalmaz(vaz, true);
    if (!input.disabled) input.focus();
    gorgessLegalulra(list);
  });
  iroZar.addEventListener("click", () => {
    vaz.iroNyitva = false;
    iroSavAlkalmaz(vaz, false);
    input.blur();
  });
  return vaz;
}

/** Az írósáv megjelenése: összecsukva egyetlen mini sor; a súgósor, a küldési cél és az átirat-sor ilyenkor rejtett (CSS). */
function iroSavAlkalmaz(m, nyitva) {
  m.iroKinyitva = nyitva;
  m.bar.classList.toggle("is-collapsed", !nyitva);
  m.app.classList.toggle("chat-input-collapsed", !nyitva);
}

/** A változó részek frissítése. A beviteli sávhoz NEM nyúl feleslegesen. */
function updateChatScreen(model, handlers, ujVaz) {
  const {
    uiLang, contentLang, rooms, currentRoomId, messages, myUid, isAdmin, isTopicLead,
    draft, sending, errorKey, errorText, installBanner, firstUnreadMessageId,
  } = model;
  // Csak olvasható szoba (2026-10-08): a tag a beviteli sávot tiltva látja, és nem válaszolhat / szerkeszthet.
  const aktSzoba = Array.isArray(rooms) ? rooms.find((r) => r && r.roomId === currentRoomId) : null;
  const aktSzobaIrhato = szobaIrhato(aktSzoba, isAdmin === true);
  // Fórum (2026-10-09): egy témán belül a téma is dönt (előzmény: soha; lezárt: csak admin).
  const aktIrhato = aktSzobaIrhato && model.temaIrhato !== false;
  const aktSzobaCsakOlvashato = Boolean(aktSzoba && aktSzoba.readOnly === true);
  // Az admin panelt admin ÉS Témafelelős is megnyithatja (2026-09-30) - a
  // panel tartalma a szerepkör szerint szűkül (lásd adminView.js).
  const isStaff = isAdmin || isTopicLead === true;

  const m = mounted;

  // A görgetési szándékot a lista ÚJRAÉPÍTÉSE ELŐTT kell megállapítani,
  // mert utána a mérés már az új tartalomra vonatkozna.
  const voltLegaljan = ujVaz || vanLegaljan(m.list);
  const szobatValtott = m.lastRoomId !== currentRoomId;

  const utolso = Array.isArray(messages) && messages.length > 0 ? messages[messages.length - 1] : null;
  const utolsoId = utolso ? utolso.messageId : null;
  const ujUzenet = utolsoId !== null && utolsoId !== m.lastMessageId;
  // A SAJÁT, frissen elküldött üzenethez mindig odagörgetünk - ez volt az
  // a hiba, amitől az elküldött üzenet néha "eltűnt" a képernyő alján.
  const sajatUjUzenet = ujUzenet && utolso && utolso.senderId === myUid;

  m.lastRoomId = currentRoomId;
  m.lastMessageId = utolsoId;

  // --- Felső rész: telepítési sáv, értesítés-felajánlás, fejléc
  m.top.innerHTML = "";
  if (installBanner) m.top.appendChild(installBanner);

  // Értesítés-felajánlás: diszkrét sáv, nem felugró ablak. A böngésző
  // amúgy is csak felhasználói gesztusra engedi az engedélykérést.
  if (model.offerNotifications) {
    const bar = el("div", { class: "notif-bar" });
    bar.appendChild(el("span", { class: "notif-text", text: t(uiLang, "notif.offer") }));
    const actions = el("div", { class: "notif-actions" });
    actions.appendChild(
      el("button", {
        type: "button",
        class: "notif-btn",
        text: t(uiLang, "notif.enable"),
        onclick: handlers.onEnableNotifications,
      })
    );
    actions.appendChild(
      el("button", {
        type: "button",
        class: "notif-dismiss",
        text: t(uiLang, "notif.dismiss"),
        onclick: handlers.onDismissNotifications,
      })
    );
    bar.appendChild(actions);
    m.top.appendChild(bar);
  }

  const header = el("div", { class: "chat-header" });
  const currentRoom = rooms.find((r) => r.roomId === currentRoomId) || null;
  const displayRoomName = currentRoom
    ? getRoomDisplayName(currentRoom, contentLang, uiLang)
    : t(uiLang, "chat.rooms");
  const cimGomb = el("button", {
    type: "button",
    class: "room-title-btn",
    onclick: handlers.onOpenRooms,
  });
  // Alszobában a fejléc a SZÜLŐ(K) nevét is mutatja (2026-10-04, tagi kérésre):
  // kis, szürke "Főszoba ❯" sor a szoba neve fölött. Mélyebb beágyazásnál a
  // teljes út ("Fő ❯ Közép ❯"), a helyhez igazítva rövidítve. Csak azokat az
  // őst látjuk, amelyek a tag szobái között szerepelnek (lásd roomTree.js).
  const utvonal = currentRoom ? szuloUtvonal(rooms, currentRoom) : [];
  if (utvonal.length > 0) {
    const utvonalSzoveg = utvonal.map((r) => getRoomDisplayName(r, contentLang, uiLang)).join(" ❯ ") + " ❯";
    cimGomb.classList.add("has-path");
    cimGomb.title = utvonalSzoveg + " " + displayRoomName;
    cimGomb.appendChild(el("span", { class: "room-title-path", text: utvonalSzoveg }));
    cimGomb.appendChild(el("span", { class: "room-title-name", text: displayRoomName + " ▾" }));
  } else {
    cimGomb.textContent = displayRoomName + " ▾";
  }
  // A "menü" szó a szobanév mellett (v157, a titkár #96 javaslata): az újonnan érkezők lássák, hogy erre kattintva nyílik a szobalista.
  cimGomb.appendChild(el("span", { class: "room-title-menu", text: t(uiLang, "room.menuWord") }));
  // Másik szobában olvasatlan üzenet van: pötty a szobaválasztó gombon.
  if ((model.unreadRoomIds || []).some((id) => id !== currentRoomId)) {
    cimGomb.appendChild(el("span", { class: "unread-dot", "aria-label": t(uiLang, "chat.unreadElsewhere"), title: t(uiLang, "chat.unreadElsewhere") }));
  }
  header.appendChild(cimGomb);

  const headerRight = el("div", { class: "chat-header-right" });
  // Keresés az üzenetekben (2026-10-04, keresesView.js).
  if (handlers.onOpenSearch) {
    headerRight.appendChild(
      el("button", {
        type: "button", class: "text-btn search-open-btn", text: "🔍",
        title: t(uiLang, "search.open"), "aria-label": t(uiLang, "search.open"),
        onclick: handlers.onOpenSearch,
      })
    );
  }
  // Közös szójegyzék (2026-10-08): minden tag megnyithatja, bővítheti.
  if (handlers.onOpenGlossary) {
    headerRight.appendChild(
      el("button", {
        type: "button", class: "text-btn glossary-open-btn", text: "📖",
        title: t(uiLang, "glossary.open"), "aria-label": t(uiLang, "glossary.open"),
        onclick: handlers.onOpenGlossary,
      })
    );
  }
  // Cetlik (2026-10-07): ha van el nem dobott cetlid, a ✉ gomb (számmal) újranyitja a felugró ablakot.
  if (handlers.onOpenNotes && Number(model.noteCount) > 0) {
    headerRight.appendChild(
      el("button", {
        type: "button", class: "text-btn note-open-btn",
        title: t(uiLang, "note.openTitle", { n: model.noteCount }),
        "aria-label": t(uiLang, "note.openTitle", { n: model.noteCount }),
        onclick: handlers.onOpenNotes,
      }, [el("span", { text: "✉" }), el("span", { class: "note-badge", text: String(model.noteCount) })])
    );
  }
  // Kezdőlap (2026-10-25, a titkár #86): minden szobából elérhető állandó bejárat a tagok kezdőlapjára.
  if (handlers.onOpenHome) {
    headerRight.appendChild(
      el("button", {
        type: "button", class: "text-btn home-open-btn", text: "🏠",
        title: t(uiLang, "home.open"), "aria-label": t(uiLang, "home.open"),
        onclick: handlers.onOpenHome,
      })
    );
  }
  // Közlemények (2026-10-25): a még tudomásul nem vett közlemény(ek) jelzése; a 📌 gomb újranyitja a felugró ablakot.
  if (handlers.onOpenAnnouncements && Number(model.announcementCount) > 0) {
    headerRight.appendChild(
      el("button", {
        type: "button", class: "text-btn note-open-btn announcement-open-btn",
        title: t(uiLang, "ann.openTitle", { n: model.announcementCount }),
        "aria-label": t(uiLang, "ann.openTitle", { n: model.announcementCount }),
        onclick: handlers.onOpenAnnouncements,
      }, [el("span", { text: "📌" }), el("span", { class: "note-badge", text: String(model.announcementCount) })])
    );
  }
  if (isStaff) {
    headerRight.appendChild(
      el("button", { type: "button", class: "text-btn", text: t(uiLang, "admin.open"), onclick: handlers.onOpenAdmin })
    );
  }
  // Súgó (2026-10-18): "Első lépések" - hely, téma, válasz; a tapasztalt tagnak nem tolakszik (kis "?").
  if (handlers.onOpenHelp) {
    headerRight.appendChild(
      el("button", { type: "button", class: "text-btn help-btn", text: "?", title: t(uiLang, "help.open"), "aria-label": t(uiLang, "help.open"), onclick: handlers.onOpenHelp })
    );
  }
  headerRight.appendChild(
    el("button", { type: "button", class: "text-btn", text: t(uiLang, "profile.title"), onclick: handlers.onOpenProfile })
  );
  headerRight.appendChild(
    el("button", { type: "button", class: "text-btn", text: t(uiLang, "common.signOut"), onclick: handlers.onSignOut })
  );
  header.appendChild(headerRight);
  m.top.appendChild(header);

  // --- Fórum (2026-10-09): témalista vagy egy téma. A témalista módban az üzenetlista és a beviteli
  // sáv nem látszik; egy megnyitott témánál a téma sávja kerül a fejléc alá, a hozzászólások pedig a
  // megszokott üzenetnézetben jönnek.
  const forumLista = model.forum === true && !model.currentTopicId;
  for (const elem of [m.list, m.previewSlot, m.replySlot, m.bar, m.hint]) elem.style.display = forumLista ? "none" : "";
  // A hibasáv a témalistán is látszik, ha van hiba (v204: az átirányító sor "nem vagy tagja" üzenete eddig a rejtett sávba került: a koppintásra semmi nem történt).
  m.errorSlot.style.display = forumLista && !(model.errorText || model.errorKey) ? "none" : "";
  if (forumLista) m.atiratSlot.style.display = "none"; // (a témalistán nincs beviteli sáv; egyébként lent frissül)
  // A küldési cél csak megnyitott, írható fórum-témában (vagy sima szobában) látszik; a szöveget lent frissítjük.
  m.cel.style.display = "none";
  // A témalista görgetési helye (2026-10-19, a titkár #35): témába lépéskor megjegyezzük, a listára visszatérve EGYSZER visszaállítjuk
  // (ugyanabban a szobában); a lista élő frissítései közben a felhasználó görgetéséhez nem nyúlunk.
  const listaLatszott = m.forumPanel.style.display !== "none";
  if (!forumLista && listaLatszott && m.forumPanel.scrollTop > 0) m.forumGorgetes = { roomId: m.forumPanelRoomId, top: m.forumPanel.scrollTop };
  m.forumPanel.style.display = forumLista ? "" : "none";
  if (forumLista) {
    renderTopicList(m.forumPanel, { ...model, szobaIrhato: aktSzobaIrhato }, handlers);
    if (!listaLatszott && m.forumGorgetes && m.forumGorgetes.roomId === model.currentRoomId) m.forumPanel.scrollTop = m.forumGorgetes.top;
    if (!listaLatszott) m.forumGorgetes = null;
    m.forumPanelRoomId = model.currentRoomId;
    m.errorSlot.innerHTML = "";
    if (model.errorText || model.errorKey) m.errorSlot.appendChild(el("div", { class: "chat-error", role: "alert", text: model.errorText || t(uiLang, model.errorKey) }));
    return;
  }
  // Nem fórum-módú szülőszobában az alszobák egysoros sávja a fejléc alatt (fórum-módúban a témalista tetején áll a teljes szakasz).
  if (model.forum !== true && handlers.onOpenSubRoom) {
    const utSav = renderSzobaUtvonal(model, handlers);
    if (utSav) m.top.appendChild(utSav);
    const alszobaSav = renderSubRooms(model, handlers, true);
    if (alszobaSav) m.top.appendChild(alszobaSav);
  }
  if (model.forum === true && model.aktTema) {
    // Útvonal-sor a téma sávja fölött (2026-10-19, a titkár #35): Szobák › Ősszoba › Szoba › Téma.
    const temaUt = renderTemaUtvonal(model, handlers);
    if (temaUt) m.top.appendChild(temaUt);
    m.top.appendChild(renderTopicBar(model, handlers));
    // A belépési cél (hirdetmény) témájának fejléce (v157, a titkár #96 javaslata): a menüből lépve is ugyanaz.
    const bejaratFej = renderBejaratFej(model, handlers);
    if (bejaratFej) m.top.appendChild(bejaratFej);
  }
  // A téma forrásmegjelölése (2026-10-18): kártya a sáv alatt.
  if (model.forum === true && model.aktTema && model.aktTema.kind === "discussion") {
    const forrasKartya = renderSourceCard(model.aktTema, model.uiLang);
    if (forrasKartya) m.top.appendChild(forrasKartya);
    const wfMegjegyzes = renderWorkflowNote(model.aktTema, model.uiLang, handlers);
    if (wfMegjegyzes) m.top.appendChild(wfMegjegyzes);
  }
  if (model.forum === true && model.aktTema && model.aktTema.kind === "discussion") {
    const szuroSor = renderPostFilter({ ...model, postTags: hasznaltCimkek(model.messages, model.tagCatalog) }, handlers);
    if (szuroSor) m.top.appendChild(szuroSor);
  }
  // Címek nyelvenként (admin): a szerkesztő a téma sávja alatt, az üzenetlista helyett jelenik meg.
  const cimSzerkeszto = model.forum === true && Boolean(model.aktTema) && ((model.titlesDraft !== null && model.titlesDraft !== undefined) || (model.tagsDraft !== null && model.tagsDraft !== undefined) || (model.katalogusDraft !== null && model.katalogusDraft !== undefined) || (model.sourceDraft !== null && model.sourceDraft !== undefined) || (model.wfPanel !== null && model.wfPanel !== undefined) || (model.atnevDraft !== null && model.atnevDraft !== undefined));
  for (const elem of [m.list, m.errorSlot, m.previewSlot, m.replySlot, m.bar, m.hint]) if (cimSzerkeszto) elem.style.display = "none";
  if (cimSzerkeszto) m.atiratSlot.style.display = "none"; // (a szerkesztő alatt nincs beviteli sáv)
  m.forumPanel.style.display = cimSzerkeszto ? "" : "none";
  if (cimSzerkeszto) {
    m.forumPanel.innerHTML = "";
    // Címkekatalógus (admin, a címkeválasztóból nyitva), címkeválasztó, különben a címek szerkesztője (2026-10-16).
    const van = (x) => x !== null && x !== undefined;
    m.forumPanel.appendChild(van(model.katalogusDraft) ? renderCatalogEditor(model, handlers) : van(model.tagsDraft) ? renderTagsEditor(model, handlers) : van(model.sourceDraft) ? renderSourceEditor(model, handlers) : van(model.wfPanel) ? renderWorkflowPanel(model, handlers) : van(model.atnevDraft) ? renderAtnevezes(model, handlers) : renderTitlesEditor(model, handlers));
    return;
  }
  // Fórumszerű megjelenés egy témán belül: teljes szélességű, egymás alatti bejegyzések.
  m.list.classList.toggle("forum-posts", model.forum === true && Boolean(model.currentTopicId));

  // --- Üzenetlista
  //
  // FONTOS, VALÓS HIBÁBÓL TANULVA (2026-09-26): korábban minden render
  // az EGÉSZ listát eldobta és újraépítette - képekkel együtt. Egy üzenet
  // után a fordítás állapota 2-3-szor változik, és minden változás
  // mind az ~50 sort újrarajzolta; gépelés közben ez érezhető megakadást
  // okozott (a japán tag jelzése: "a képernyő egy pillanatra megáll").
  // Most minden sor egy ALÁÍRÁST kap mindabból, amit megjelenít; ha az
  // nem változott, a meglévő DOM-elem marad (a kép nem töltődik újra), és
  // csak a ténylegesen változott sor épül újra.
  if (m.sorHandlers !== handlers) {
    m.sorCache = new Map();
    m.sorHandlers = handlers;
  }
  const ujSorok = [];
  if (!currentRoomId && model.roomsMegerositetlen === true) {
    // v181: a szobalista nem igazolt / hibás (akadozó hálózat): nem "Még nincsenek szobák", hanem jelzés + újrapróba.
    ujSorok.push(renderListaJelzes(uiLang, handlers.onRetryList));
  } else if (!currentRoomId) {
    // roomsLoaded === false: a szobalista még nem érkezett meg ("Betöltés…", nem "Még nincsenek szobák"); undefined (régi hívók): a korábbi viselkedés.
    ujSorok.push(
      el("div", {
        class: "empty-state" + (model.roomsLoaded === false ? " rooms-loading" : ""),
        text: model.roomsLoaded === false ? t(uiLang, "common.loading") : isStaff ? t(uiLang, "chat.noRoomsAdmin") : t(uiLang, "chat.noRoomsMember"),
      })
    );
  } else if (messages === null) {
    ujSorok.push(el("div", { class: "empty-state", text: t(uiLang, "chat.loadingMessages") }));
  } else if (messages.length === 0 && model.messagesMegerositetlen === true) {
    // v181: üres, de a szerver által nem igazolt / hibás lista: nem "Még nincs üzenet. Írd meg az elsőt." (a hozzászólás megvan, csak a válasz nem jött meg).
    ujSorok.push(renderListaJelzes(uiLang, handlers.onRetryList));
  } else if (messages.length === 0) {
    ujSorok.push(el("div", { class: "empty-state", text: t(uiLang, "chat.noMessages") }));
  } else {
    const ujCache = new Map();
    const ma = new Date().toDateString();
    // Szerkesztés/törlés (2026-10-02): a szerkesztés állapota az üzenet
    // dokumentumán KÍVÜL élő, külön modell-mező - lásd sorAlairas() és
    // renderMessage() megjegyzését.
    const editState = {
      editingMessageId: model.editingMessageId,
      editingText: model.editingText,
      editingBusy: model.editingBusy,
      editingErrorKey: model.editingErrorKey,
      editingErrorText: model.editingErrorText,
      isAdmin,
      isStaff,
      showOriginalIds: model.showOriginalIds,
      egymasMellett: model.egymasMellett === true,
      retryBusyIds: model.retryBusyIds,
      szavazok: model.szavazok || {},
      // Csak olvasható szoba (2026-10-08): a tag nem válaszolhat és nem szerkeszthet.
      irhato: aktIrhato,
      // Hozzászólás-címkék (2026-10-16): csak fórum-témán belül, az előzményben nem.
      forumTema: model.forum === true && Boolean(model.aktTema) && model.aktTema.kind === "discussion",
      tagKezelo: model.tagKezelo === true,
      tagCatalog: model.tagCatalog || [],
      postTagging: model.postTagging || null,
      postLinking: model.postLinking || null,
      fixEditor: model.fixEditor || null,
      fixList: model.fixList || null,
      atiratSzerk: model.atiratSzerk || null,
      modernAllapot: model.modernAllapot || null,
      postReviewing: model.postReviewing || null,
      uzenetek: messages,
      postSzuro: model.postSzuro || [],
    };
    // A szűrő a megjelenítést szűri, az "összes üzenet" (idézetek, olvasatlan-határ) változatlan marad.
    const latszo = new Set(uzenetSzures(messages, model.postSzuro, model.tagCatalog).map((x) => x.messageId));
    const szuroAktiv = editState.forumTema && (model.postSzuro || []).length > 0;
    const katalogusKulcs = JSON.stringify(editState.tagCatalog);
    if (szuroAktiv && !messages.some((x) => latszo.has(x.messageId))) ujSorok.push(el("div", { class: "empty-state", text: t(uiLang, "forum.filterNoMatch") }));
    for (const msg of messages) {
      // Fórum-átállás (2026-10-09): a régi, darabolva küldött hosszú üzenet összevont részei nem
      // látszanak - a szövegük az első részben, egy bejegyzésként áll (admin-scripts/forum-atallas.js).
      if (msg.mergedInto) continue;
      if (szuroAktiv && !latszo.has(msg.messageId)) continue;
      const sajatSzerkesztesAllapot =
        editState.editingMessageId === msg.messageId
          ? [editState.editingText, editState.editingBusy, editState.editingErrorKey, editState.editingErrorText]
          : null;
      const mostMs = Date.now();
      const megjelenitesiAllapot = [
        editState.showOriginalIds instanceof Set && editState.showOriginalIds.has(msg.messageId),
        editState.egymasMellett,
        editState.retryBusyIds instanceof Set && editState.retryBusyIds.has(msg.messageId),
        szerkesztesiIdonBelul(msg, mostMs),
        torlesiIdonBelul(msg, mostMs),
        aktIrhato,
        // Hozzászólás-címkék: a katalógus (átnevezés), a jogosultság és az épp nyitott választó vázlata a sor részei.
        katalogusKulcs, editState.forumTema, editState.tagKezelo, editState.postSzuro.join(","),
        editState.postTagging && editState.postTagging.messageId === msg.messageId ? editState.postTagging : null,
        editState.forumTema ? kapcsolatKulcs(msg, messages) : "",
        editState.postLinking && editState.postLinking.messageId === msg.messageId ? editState.postLinking : null,
        editState.postReviewing && editState.postReviewing.messageId === msg.messageId ? editState.postReviewing : null,
        // Fordításjavaslat (2026-10-19): a nyitott szerkesztő / lista vázlata a sor része.
        editState.fixEditor && editState.fixEditor.messageId === msg.messageId ? editState.fixEditor : null,
        editState.fixList && editState.fixList.messageId === msg.messageId ? editState.fixList : null,
        // Átirat (2026-10-19): a nyitott javító szerkesztő vázlata a sor része.
        editState.atiratSzerk && editState.atiratSzerk.messageId === msg.messageId ? editState.atiratSzerk : null,
        // Átirat modernizálása (v205): a folyamat / hiba állapota a sor része.
        editState.modernAllapot && editState.modernAllapot.messageId === msg.messageId ? editState.modernAllapot : null,
        // Karakterszavazó (2026-10-30): a betöltött szavazat-állapot a sor része.
        msg.poll && editState.szavazok ? editState.szavazok[msg.messageId] || null : null,
      ];
      const alairas = sorAlairas(msg, contentLang, uiLang, myUid, messages, ma, sajatSzerkesztesAllapot, megjelenitesiAllapot);
      const regi = msg.messageId ? m.sorCache.get(msg.messageId) : null;
      const node =
        regi && alairas !== null && regi.alairas === alairas
          ? regi.node
          : renderMessage(msg, contentLang, uiLang, myUid, handlers, messages, editState);
      if (msg.messageId) ujCache.set(msg.messageId, { alairas, node });

      // "Meddig olvastad" elválasztó: pontosan az első, a szoba
      // megnyitásakor még olvasatlan üzenet elé kerül (lásd main.js
      // openRoom() - onnan jön a firstUnreadMessageId). Gyorsítótárazva,
      // mint a lapfej, hogy ne épüljön újra minden rendernél.
      if (firstUnreadMessageId && msg.messageId === firstUnreadMessageId) {
        const valasztoKulcs = `${firstUnreadMessageId}|${uiLang}`;
        if (!m.olvasatlanValaszto || m.olvasatlanValasztoKulcs !== valasztoKulcs) {
          m.olvasatlanValaszto = el("div", { class: "unread-divider", text: t(uiLang, "chat.unread") });
          m.olvasatlanValasztoKulcs = valasztoKulcs;
        }
        ujSorok.push(m.olvasatlanValaszto);
      }
      ujSorok.push(node);
    }
    m.sorCache = ujCache;
  }
  // Ha az aktuális lapon nincs (már) olvasatlan üzenet - másik szoba, vagy
  // a jelölt üzenet kilapozódott -, a régi elválasztó ne maradjon a
  // gyorsítótárban, különben egy véletlen azonosító-egyezésnél visszatérne.
  if (!firstUnreadMessageId || !messages || !messages.some((msg) => msg.messageId === firstUnreadMessageId)) {
    m.olvasatlanValaszto = null;
    m.olvasatlanValasztoKulcs = null;
  }
  // --- A lista teteje: "Korábbi üzenetek" gomb, vagy "A beszélgetés eleje".
  // Az elem gyorsítótárazott (mint a sorok), hogy ne épüljön újra minden
  // rendernél - különben a lista eleje minden alkalommal megmozdulna.
  if (currentRoomId && Array.isArray(messages) && messages.length > 0) {
    const fejKulcs = `${model.hasOlder ? 1 : 0}|${model.loadingOlder ? 1 : 0}|${uiLang}`;
    if (!m.lapFej || m.lapFejKulcs !== fejKulcs) {
      m.lapFej = model.hasOlder
        ? el("button", {
            type: "button",
            class: "load-older",
            text: t(uiLang, model.loadingOlder ? "chat.loadingOlder" : "chat.loadOlder"),
            disabled: Boolean(model.loadingOlder),
            onclick: () => handlers.onLoadOlder && handlers.onLoadOlder(),
          })
        : el("div", { class: "conversation-start", text: t(uiLang, "chat.conversationStart") });
      m.lapFejKulcs = fejKulcs;
    }
    ujSorok.unshift(m.lapFej);
  }

  // HORGONY (v188, Tamás: "újrafordítás kéréskor, egymás mellett gombra kattintáskor elmegy a fókusz a legutolsó bejegyzésre... maradjon a fókusz
  // ott, ahol kattintottunk"): az "Egymás mellett" minden sort újraépít (más magasság), ezért a nézet elmozdult. A kattintott sort (különben az első
  // látható sort) a DOM-módosítás ELŐTT megjegyezzük, utána a görgetést úgy korrigáljuk, hogy ugyanott maradjon a képernyőn.
  const horgony = !voltLegaljan && !szobatValtott && !ujVaz ? horgonyMegjegyzese(m) : null;

  // Görgetés-rögzítés: ha a lista ELEJÉRE kerültek üzenetek (régebbi lap),
  // a felhasználó ugyanott maradjon, ahol olvasott - ne ugorjon a lap tetejére.
  const elozoMagassag = m.list.scrollHeight;
  const ujElsoId = Array.isArray(messages) && messages.length > 0 ? messages[0].messageId : null;
  const elejereKerult = !szobatValtott && !ujVaz && m.elsoUzenetId && ujElsoId && ujElsoId !== m.elsoUzenetId;
  m.elsoUzenetId = ujElsoId;

  // Minimális DOM-módosítás: a helyén lévő elemhez nem nyúlunk, csak a
  // hiányzót szúrjuk be, és a fölöslegeset vesszük ki.
  ujSorok.forEach((node, i) => {
    const jelenlegi = m.list.childNodes[i];
    if (jelenlegi !== node) m.list.insertBefore(node, jelenlegi || null);
  });
  while (m.list.childNodes.length > ujSorok.length) m.list.lastChild.remove();
  if (elejereKerult && !voltLegaljan) {
    m.list.scrollTop += m.list.scrollHeight - elozoMagassag;
  } else if (horgony) {
    horgonyVisszaallitasa(m, horgony);
  }

  // --- Hibasáv
  m.errorSlot.innerHTML = "";
  if (errorText || errorKey) {
    m.errorSlot.appendChild(el("div", { class: "chat-error", text: errorText || t(uiLang, errorKey) }));
  }

  // --- Kiválasztott kép előnézete (a beviteli sáv FÖLÖTT)
  m.previewSlot.innerHTML = "";
  if (model.pendingImage) {
    const elonezet = el("div", { class: "image-preview" });
    elonezet.appendChild(
      el("img", { class: "image-preview-img", src: model.pendingImage.previewUrl, alt: "" })
    );
    // A további képek (v211) az első kép `extras` listáján utaznak (így a szobánkénti félretétel és a felszabadítás közös).
    const tobbiKep = Array.isArray(model.pendingImage.extras) ? model.pendingImage.extras : [];

    const info = el("div", { class: "image-preview-info" });
    if (model.uploadProgress !== null && model.uploadProgress !== undefined) {
      info.appendChild(
        el("span", { text: t(uiLang, "image.uploading", { percent: model.uploadProgress }) })
      );
    } else {
      const kb = Math.round(model.pendingImage.compressedSize / 1024);
      // A "@2"-nél az EREDETI, tömörítetlen fájl megy fel: ezt jelezzük, hogy a
      // megjelenített méret ne tűnjön tömörítettnek (2026-10-06).
      const cimke = model.pendingImage.eredeti ? ` · ${t(uiLang, "image.originalBadge")}` : "";
      info.appendChild(el("span", { text: `${kb} kB${cimke}` }));
    }
    elonezet.appendChild(info);

    elonezet.appendChild(
      el("button", {
        type: "button",
        class: "image-preview-remove",
        text: "✕",
        title: t(uiLang, "image.remove"),
        disabled: sending,
        onclick: () => handlers.onRemoveImage(0),
      })
    );
    m.previewSlot.appendChild(elonezet);
    // A további képek (v211): mindegyik saját sorban, saját eltávolító gombbal.
    tobbiKep.forEach((tovabbi, i) => {
      const sor = el("div", { class: "image-preview image-preview-extra" });
      sor.appendChild(el("img", { class: "image-preview-img", src: tovabbi.previewUrl, alt: "" }));
      const sorInfo = el("div", { class: "image-preview-info" });
      sorInfo.appendChild(el("span", { text: `${Math.round(tovabbi.compressedSize / 1024)} kB` }));
      sor.appendChild(sorInfo);
      sor.appendChild(el("button", { type: "button", class: "image-preview-remove", text: "✕", title: t(uiLang, "image.remove"), disabled: sending, onclick: () => handlers.onRemoveImage(i + 1) }));
      m.previewSlot.appendChild(sor);
    });
    if (tobbiKep.length > 0) m.previewSlot.appendChild(el("div", { class: "hint image-count-hint", text: t(uiLang, "image.countOfMax", { n: 1 + tobbiKep.length, max: MAX_KEPEK }) }));
    // A csatolás módja (2026-10-18, AI-titkári audit: "a parancsok szimbólum nélkül is elérhetők legyenek"): normál / szövegfelismerés (@2) /
    // képjavítás (@1). A választó a meglévő parancsokat írja a mezőbe - ugyanazt, amit a gyakorlott tag begépel -, így a szerveroldal változatlan.
    const feltoltesAlatt = model.uploadProgress !== null && model.uploadProgress !== undefined;
    // Több képnél nincs "@1" / "@2" mód: az egyetlen képre vonatkozik (a szerver sem indítja el).
    if (handlers.onPickImageMode && !feltoltesAlatt && tobbiKep.length === 0) m.previewSlot.appendChild(kepMod(draft, uiLang, sending, handlers));
  }

  // PDF-melléklet előnézete (2026-10-10): név + méret + eltávolítás (a képnél az előnézeti kép áll helyette).
  if (model.pendingFile) {
    const elonezet = el("div", { class: "image-preview file-preview" });
    elonezet.appendChild(el("span", { class: "msg-file-icon", text: "📄" }));
    const info = el("div", { class: "image-preview-info" });
    info.appendChild(el("div", { class: "msg-file-name", text: model.pendingFile.name }));
    info.appendChild(el("div", { class: "msg-file-size", text: fajlMeret(model.pendingFile.size) }));
    elonezet.appendChild(info);
    elonezet.appendChild(el("button", { type: "button", class: "image-preview-remove", text: "✕", title: t(uiLang, "file.remove"), disabled: sending, onclick: handlers.onRemoveFile }));
    m.previewSlot.appendChild(elonezet);
  }

  // --- "Válasz erre:" jelzés a beviteli sáv fölött
  m.replySlot.innerHTML = "";
  if (model.replyTo) {
    const r = model.replyTo;
    const sav = el("div", { class: "reply-bar" });

    const info = el("div", { class: "reply-bar-info" });
    info.appendChild(
      el("div", {
        class: "reply-bar-name",
        text: t(uiLang, "reply.replyingTo", {
          name: feladoNev(r, contentLang, uiLang),
        }),
      })
    );
    info.appendChild(el("div", { class: "reply-bar-text", text: r.excerpt || "" }));
    sav.appendChild(info);

    sav.appendChild(
      el("button", {
        type: "button",
        class: "reply-bar-cancel",
        text: "✕",
        title: t(uiLang, "reply.cancel"),
        onclick: handlers.onCancelReply,
      })
    );
    m.replySlot.appendChild(sav);
  }

  // --- A beviteli sáv állapota (az ELEM megmarad, csak a tulajdonságai
  // frissülnek - így a fókusz és a kurzor a helyén marad)
  m.attachBtn.title = t(uiLang, "attach.imageOrPdf");
  // Feliratos gombok (2026-10-18, tagi jelzés: a "+" és a "➤" jelet nem mindenki érti): az ikon alatt kis felirat áll.
  gombTartalom(m.attachBtn, "＋", t(uiLang, "chat.attachShort"));
  m.attachBtn.setAttribute("aria-label", t(uiLang, "attach.imageOrPdf"));
  // Több kép (v211): a "＋" addig használható, amíg a bejegyzésen kevesebb mint MAX_KEPEK kép van; PDF mellett nem.
  const kepSzam = model.pendingImage ? 1 + (Array.isArray(model.pendingImage.extras) ? model.pendingImage.extras.length : 0) : 0;
  m.attachBtn.disabled = !currentRoomId || sending || kepSzam >= MAX_KEPEK || Boolean(model.pendingFile) || !aktIrhato;
  m.input.placeholder = aktIrhato ? t(uiLang, "chat.inputPlaceholder") : t(uiLang, aktSzobaIrhato ? (model.aktTema && model.aktTema.kind === "history" ? "forum.historyReadOnly" : model.aktTema && model.aktTema.locked === true ? "forum.lockedHint" : "forum.closedHint") : "chat.readOnlyRoom");
  gombTartalom(m.sendBtn, sending ? "…" : "➤", t(uiLang, "chat.send"));
  m.sendBtn.setAttribute("aria-label", t(uiLang, "chat.send"));
  m.sendBtn.disabled = !currentRoomId || sending || !aktIrhato;
  // Az írósáv összecsukása / kinyitása (v183): szoba- vagy témaváltáskor és sikeres küldés után összecsukva; válasz, csatolmány, átirat,
  // küldés közben (és hibás küldés után a megmaradt szöveggel) nyitva. A mini sor a piszkozat elejét is mutatja.
  const iroHely = `${currentRoomId || ""}|${model.currentTopicId || ""}`;
  if (m.iroHely !== iroHely) { m.iroHely = iroHely; m.iroNyitva = false; }
  if (m.elozoKuldes && !sending && !(draft || "").trim()) m.iroNyitva = false;
  m.elozoKuldes = Boolean(sending);
  const iroKenyszer = Boolean(model.replyTo) || Boolean(model.pendingImage) || Boolean(model.pendingFile) || Boolean(model.atiratNyelv) || Boolean(sending);
  iroSavAlkalmaz(m, m.iroNyitva || iroKenyszer);
  const piszkozat = (draft || "").trim().replace(/\s+/g, " ");
  const miniSzoveg = piszkozat ? "✎ " + (piszkozat.length > 60 ? piszkozat.slice(0, 60) + "…" : piszkozat) : "✎ " + (aktIrhato ? t(uiLang, "chat.inputPlaceholder") : m.input.placeholder);
  if (m.iroMini.textContent !== miniSzoveg) m.iroMini.textContent = miniSzoveg;
  m.iroMini.disabled = !currentRoomId || !aktIrhato;
  m.iroMini.setAttribute("aria-label", miniSzoveg);
  m.iroZar.setAttribute("aria-label", t(uiLang, "entry.collapse"));
  m.iroZar.title = t(uiLang, "entry.collapse");
  // A súgósor jelzi előre, ha a szöveg több részletben megy el - így a
  // felhasználó nem a küldés után szembesül vele.
  const darabszam = (draft || "").trim() ? splitMessage(draft, darabolasiKorlat(Boolean(model.forum && model.currentTopicId))).length : 1;
  const celSzoveg = aktIrhato && currentRoomId ? kuldesiCel(model, uiLang, contentLang) : "";
  if (m.cel.textContent !== celSzoveg) m.cel.textContent = celSzoveg;
  m.cel.style.display = celSzoveg && !forumLista && !cimSzerkeszto ? "" : "none";
  // Átirat-sor (2026-10-19): csak írható helyen, kép / PDF nélkül (az átirat szöveg); a nyelvválasztó csak a lista változásakor épül újra.
  const atiratKell = aktIrhato && Boolean(currentRoomId) && !forumLista && !cimSzerkeszto && !model.pendingImage && !model.pendingFile;
  const atiratKulcs = atiratKell ? [uiLang, contentLang, model.atiratNyelv || "", Object.keys(CHAT_LANGUAGES).join(",")].join("|") : "";
  if (m.atiratKulcs !== atiratKulcs) {
    m.atiratKulcs = atiratKulcs;
    m.atiratSlot.replaceChildren();
    const sor = atiratKell
      ? renderAtiratSor({ uiLang, atiratNyelv: model.atiratNyelv || null, contentLang, chatNyelvek: CHAT_LANGUAGES }, handlers)
      : null;
    if (sor) m.atiratSlot.appendChild(sor);
  }
  m.atiratSlot.style.display = m.atiratSlot.firstChild ? "" : "none";
  const hintSzoveg = (darab) => (!aktSzobaIrhato
    ? t(uiLang, "chat.readOnlyRoom")
    : !aktIrhato
    ? m.input.placeholder
    : aktSzobaCsakOlvashato
    // Az admin is lássa, hogy a tagok itt nem írhatnak (2026-10-08).
    ? t(uiLang, "chat.readOnlyRoomAdmin")
    : darab > 1
    ? t(uiLang, "chat.tooLongSplit", { count: darab })
    // A "#" nem-fordítás jelölő mellett a Markdown-formázásra is emlékeztet
    // (2026-10-04) - e nélkül a funkció észrevétlen maradna.
    : `${t(uiLang, "chat.inputHint")} · ${t(uiLang, "chat.markdownHint")}`);
  m.hint.textContent = hintSzoveg(darabszam);
  // Gépelés közbeni frissítéshez (lásd buildSkeleton: frissitsdADarabolasiHintet): csak a "szabad írás" állapotban van értelme.
  m.hintKontextus.alap = aktSzobaIrhato && aktIrhato && !aktSzobaCsakOlvashato ? hintSzoveg(1) : null;
  m.hintKontextus.korlat = darabolasiKorlat(Boolean(model.forum && model.currentTopicId));
  m.hintKontextus.lang = uiLang;
  m.hintKontextus.darab = darabszam;

  // A textarea tiltott állapota elveszi a fókuszt, ezért CSAK akkor
  // állítjuk, ha tényleg változik.
  const tiltando = !currentRoomId || Boolean(sending) || !aktIrhato;
  if (m.input.disabled !== tiltando) m.input.disabled = tiltando;

  // Az értéket CSAK a program saját változtatásakor írjuk (pl. sikeres
  // küldés után ürítjük): akkor, ha a draft eltér attól, amit a mező
  // legutóbb maga jelentett. A mező PILLANATNYI értékével NEM hasonlítunk -
  // IME-kompozíció alatt az eltérhet, és a visszaírás megduplázta a
  // szöveget (lásd a buildSkeleton "bevitel" magyarázatát).
  const ujErtek = draft || "";
  const b = m.bevitel;
  if (ujErtek !== b.jelentett) {
    if (b.komponal) {
      b.fuggo = ujErtek;
    } else {
      m.input.value = ujErtek;
      b.jelentett = ujErtek;
      m.autoGrow();
    }
  } else {
    b.fuggo = null;
  }

  // --- Görgetés
  //
  // Szobaváltáskor, ha van "meddig olvastad" elválasztó, ODA görgetünk
  // legalulra görgetés HELYETT - így a felhasználó pontosan onnan
  // folytathatja, ahol abbahagyta, nem a legfrissebb üzenetnél landol
  // (ahonnan a köztes olvasatlanokat könnyen elmulasztaná).
  //
  // Minden más esetben: csak akkor mozdítjuk a listát, ha a felhasználó
  // amúgy is az alján volt, vagy szobát váltott (és nincs elválasztó),
  // vagy ÉPP MOST küldött üzenetet. Ha feljebb olvasgat a történetben, egy
  // beérkező üzenet nem rántja el alulra.
  if (szobatValtott && m.olvasatlanValaszto) {
    gorgessOlvasatlanhoz(m.olvasatlanValaszto);
    for (const kep of m.list.querySelectorAll("img")) {
      if (kep.complete || kep.dataset.gorgetFigyelo) continue;
      kep.dataset.gorgetFigyelo = "1";
      kep.addEventListener("load", () => gorgessOlvasatlanhoz(m.olvasatlanValaszto), { once: true });
      kep.addEventListener("error", () => gorgessOlvasatlanhoz(m.olvasatlanValaszto), { once: true });
    }
  } else if (voltLegaljan || szobatValtott || sajatUjUzenet) {
    gorgessLegalulra(m.list);

    // A képek MÉRETE csak a betöltésük után derül ki, és akkor megnő a
    // lista magassága - a korábban beállított görgetés így elavulttá
    // válik, és a legalsó üzenet a látható rész alá csúszik. Ezért a
    // betöltés befejeztével újra odagörgetünk.
    for (const kep of m.list.querySelectorAll("img")) {
      if (kep.complete) continue;
      // A sorok megmaradnak a renderek között: egy képre csak egyszer
      // akasztunk figyelőt, különben rendenként gyűlnének.
      if (kep.dataset.gorgetFigyelo) continue;
      kep.dataset.gorgetFigyelo = "1";
      kep.addEventListener("load", () => gorgessLegalulra(m.list), { once: true });
      // Hibás kép esetén is változik az elrendezés (helykitöltő tűnik el).
      kep.addEventListener("error", () => gorgessLegalulra(m.list), { once: true });
    }
  }
}

/**
 * Görgetés a lista aljára, a késői elrendezés-változásokat is kivárva.
 *
 * Két képkockán át ismételjük, mert a beviteli mező magassága, a
 * képelőnézet megjelenése és a mobil billentyűzet mind ELTOLJA a lista
 * alját azután, hogy az üzenetek bekerültek. Egyetlen görgetés ilyenkor
 * egy elavult pozícióra visz, és a legutolsó üzenet a látható rész alá
 * csúszik - pontosan ez volt a hiba, amitől az elküldött üzenet néha nem
 * látszott.
 */
function gorgessLegalulra(list) {
  const gorget = () => { list.scrollTop = list.scrollHeight; };
  gorget();
  requestAnimationFrame(() => {
    gorget();
    requestAnimationFrame(gorget);
  });
}

/**
 * Görgetés a "meddig olvastad" elválasztóhoz, a lista TETEJÉHEZ igazítva
 * (nem középre): így az elválasztó FÖLÖTTI néhány már olvasott üzenet is
 * látszik, ami visszaadja a szövegkörnyezetet - nem csak azt jelzi, hogy
 * "itt kezdődik az új", hanem azt is, mi előzte meg.
 */
function gorgessOlvasatlanhoz(elem) {
  const gorget = () => elem.scrollIntoView({ block: "start" });
  gorget();
  requestAnimationFrame(() => {
    gorget();
    requestAnimationFrame(gorget);
  });
}

/** Alulról felcsúszó szobaváltó panel. */
export function renderRoomSwitcher(app, { uiLang, contentLang, rooms, currentRoomId, unreadRoomIds = [] }, handlers) {
  const backdrop = el("div", { class: "sheet-backdrop open" });
  const sheet = el("div", { class: "sheet" });

  sheet.appendChild(el("div", { class: "sheet-handle" }));
  sheet.appendChild(el("h2", { text: t(uiLang, "chat.rooms") }));
  // Szoba-átfogó munkalista (2026-10-18): a tag szobáinak státuszos témái, határidővel.
  if (handlers.onOpenWorkList) {
    sheet.appendChild(el("button", { type: "button", class: "neutral-btn worklist-open", text: "📋 " + t(uiLang, "worklist.title"), onclick: handlers.onOpenWorkList }));
  }
  // Elbírálandó fordításjavaslatok (2026-10-19): a hozzászólások, amelyeken nyitott javaslat vár, és amelyeket a tag elbírálhat.
  if (handlers.onOpenSuggestions) {
    sheet.appendChild(el("button", { type: "button", class: "neutral-btn worklist-open", text: "✎ " + t(uiLang, "suggest.title"), onclick: handlers.onOpenSuggestions }));
  }

  const listBox = el("div", { class: "sheet-list" });
  if (rooms.length === 0) {
    listBox.appendChild(el("p", { class: "hint", text: t(uiLang, "chat.noRoomsMember") }));
  } else {
    // Beágyazott szobák (2026-09-28): a listát fa-sorrendbe rendezzük - egy
    // főszoba (kategória) után közvetlenül az alszobái következnek,
    // behúzva. `szobaFaSorrend` sosem hagy ki szobát, körkörös adathiba
    // esetén is (lásd roomTree.js).
    const szulok = szuloSzobaIdk(rooms);
    for (const { room, melyseg } of szobaFaSorrend(rooms)) {
      const olvasatlan = unreadRoomIds.includes(room.roomId) && room.roomId !== currentRoomId;
      const gomb = el("button", {
        type: "button",
        // room-parent: főszoba, aminek van alszobája (vastagon); room-sub: alszoba
        // (vékonyabban, bal oldali vonallal) - 2026-10-04, tagi kérésre vizuálisan is.
        class: "list-item" + (melyseg > 0 ? " room-sub" : "") + (szulok.has(room.roomId) ? " room-parent" : "") +
          (room.roomId === currentRoomId ? " current" : "") + (olvasatlan ? " unread" : ""),
        text:
          (melyseg > 0 ? "\u21B3 " : "") +
          getRoomDisplayName(room, contentLang || uiLang, uiLang) +
          (room.archived ? " ⊘" : ""),
        onclick: () => handlers.onSelectRoom(room.roomId),
      });
      if (melyseg > 0) gomb.style.paddingLeft = 16 + melyseg * 18 + "px";
      // Olvasatlan üzenet ebben a szobában: pötty (a képernyőolvasónak felirattal).
      if (olvasatlan) {
        gomb.appendChild(el("span", { class: "unread-dot", "aria-label": t(uiLang, "chat.unread"), title: t(uiLang, "chat.unread") }));
      }
      listBox.appendChild(gomb);
    }
  }
  sheet.appendChild(listBox);

  sheet.appendChild(
    el("button", { type: "button", class: "text-btn sheet-close", text: t(uiLang, "admin.close"), onclick: handlers.onClose })
  );

  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) handlers.onClose();
  });

  backdrop.appendChild(sheet);
  app.appendChild(backdrop);
}
